import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import { fetchUsStockSymbols, fetchPolygonSnapshotPrice, fetchPolygonSnapshotPriceses } from '../../composables/fetchPolygon';
import { useMarketPrices } from '../../context/MarketPriceContext';
import ManageCashModal from './ManageCashModal';
import EditHoldingAllocationModal from './EditHoldingAllocationModal'
import ReportIssueButton from '../ReportIssueButton'; // หรือ path ที่ถูกต้องของคุณ
import { showApiErrorToast } from '../../utils/apiErrorToast';
export type TransactionType =
  | 'BUY' | 'SELL' | 'DIVIDEND' | 'PROFIT_INCOME'
  | 'OPENING_POSITION' | 'CASH_DEPOSIT' | 'CASH_WITHDRAW' | 'ADJUST_HOLDING';

// ✅ 1. Interface และ State สำหรับเก็บข้อมูลสรุปเพื่อโชว์ใน Popup ยินดีด้วย!
interface SummaryItem {
  symbol: string;
  oldQty: number;
  oldAvgPrice: number;
  newQty: number;
  newAvgPrice: number;
  // สัดส่วนต้นทุน (cost-based weight %) เทียบกับต้นทุนรวมของพอร์ตย่อยนั้น ก่อน/หลังทำรายการ
  // ใช้ฐานต้นทุนแทนมูลค่าตลาด เพราะข้อมูลที่มีอยู่แล้วตอนนี้คือ qty/avgPrice ไม่ต้องยิง fetch ราคาตลาดเพิ่ม
  oldRatio: number;
  newRatio: number;
  hasBuy: boolean;
  hasSell: boolean;
  // มีค่าเฉพาะตอนมีรายการขาย (SELL) ของ symbol/พอร์ตย่อยนี้อยู่ในคิว คำนวณจาก WAC เดิมก่อนขาย (oldAvgPrice)
  realizedGain: number | null;
  realizedGainPercent: number | null;
}

export interface TransactionItem {
  id: string;
  date: string;
  type: TransactionType;
  symbol: string;
  quantity?: number;
  price?: number;
  amount?: number;
  fee?: number;
  tax?: number;
  idempotencyKey: string;
  subPortId: string; // ✅ เพิ่มบรรทัดนี้
  isNewStock?: boolean; // ✅ เพิ่ม flag เช็คว่าเป็นหุ้นตัวใหม่หรือไม่
  portfolioName?: string; // ✅ เพิ่มเก็บชื่อพอร์ตหลัก
  subPortName?: string;   // ✅ เพิ่มเก็บชื่อพอร์ตย่อย
}

interface SellBuyStockModalProps {
  isOpen: boolean;
  onClose: () => void;
  portfolios: any[];
  initialPortfolioId?: string;
  initialSubPortfolioId?: string;
  onConfirm: (portfolioId: string, subPortfolioId: string, transactions: TransactionItem[]) => Promise<void>;
  onAdjustCash?: (portfolioId: string, amount: number, transactionDate: any) => Promise<void>;
  initialSymbol?: string;
  initialPrice?: number; // ✅ 1. เพิ่มบรรทัดนี้เข้ามา
  onRefresh?: () => void | Promise<any[] | void>; // ✅ ถ้า resolve เป็น array ให้ถือเป็นข้อมูลพอร์ตล่าสุดที่ fetch มาจริง
  initialQuantity?: number; // ✅ เพิ่ม
  isSellAll?: boolean;      // ✅ เพิ่ม
  // พอร์ตคริปโต: โชว์ใน dropdown ด้วยให้ผู้ใช้เห็นพอร์ตครบ แต่เลือกแล้วจะสลับไปหน้าซื้อขายเหรียญ
  cryptoPortfolios?: any[];
  onSwitchToCrypto?: (portfolioId: string) => void;
}

// ✅ วันที่วันนี้ตามเวลาเครื่อง (YYYY-MM-DD) — ไม่ใช้ toISOString เพราะเป็น UTC จะได้วันที่ผิดช่วงเช้าก่อน 7 โมง (เวลาไทย)
const getTodayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

const SellBuyStockModal: React.FC<SellBuyStockModalProps> = ({
  isOpen,
  onClose,
  portfolios,
  initialPortfolioId,
  initialSubPortfolioId,
  onConfirm,
  onAdjustCash,
  initialSymbol,
  initialPrice,
  onRefresh,
  initialQuantity,
  cryptoPortfolios = [],
  onSwitchToCrypto,
  isSellAll,
}) => {
  // --- State ทั้งหมด ---
  const [selectedPortId, setSelectedPortId] = useState<string>(initialPortfolioId || '');
  const [selectedSubPortId, setSelectedSubPortId] = useState<string>(initialSubPortfolioId || '');

  const [isEditingCash, setIsEditingCash] = useState(false);
  const [cashInput, setCashInput] = useState<string>('');
  const [isAdjustingCash, setIsAdjustingCash] = useState(false);

  const [transactions, setTransactions] = useState<TransactionItem[]>([]);
  const [type, setType] = useState<TransactionType>('BUY');
  const [symbol, setSymbol] = useState('');
  const [date, setDate] = useState(getTodayStr());
  const [quantity, setQuantity] = useState<number | ''>('');
  const [price, setPrice] = useState<number | ''>('');
  const [amount, setAmount] = useState<number | ''>('');
  const [fee, setFee] = useState<number | ''>('');
  const [tax, setTax] = useState<number | ''>('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isAnimatingSymbol, setIsAnimatingSymbol] = useState(false);
  const [searchTerm, setSearchTerm] = useState(''); // เพิ่ม state นี้ครับ
  const [isForcedNewStock, setIsForcedNewStock] = useState(false);
  const [isManageCashOpen, setIsManageCashOpen] = useState(false);
  const [isAllocationModalOpen, setIsAllocationModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<{ symbol: string, name: string, price?: number }[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [hotStocks, setHotStocks] = useState<{ symbol: string, name: string, price: number }[]>([]);
  const [hotLoading, setHotLoading] = useState(false);
  const [typingTimeout, setTypingTimeout] = useState<NodeJS.Timeout | null>(null);
  const POPULAR_SYMBOLS = [
    'AAPL', 'MSFT', 'GOOG', 'AMZN', 'TSLA', 'NVDA', 'META', 'BRK.B', 'JPM', 'V', 'UNH', 'HD', 'PG', 'MA', 'XOM', 'LLY', 'AVGO', 'COST', 'ABBV', 'PEP', 'KO', 'MRK', 'WMT', 'BAC', 'ADBE', 'DIS', 'PFE', 'NFLX', 'T', 'INTC', 'CSCO', 'CMCSA', 'ABT', 'CRM', 'CVX', 'MCD', 'QCOM', 'ACN', 'DHR', 'TXN', 'NEE', 'NKE', 'TMO', 'LIN', 'WFC', 'MDT', 'BMY', 'HON', 'UNP', 'PM', 'ORCL', 'AMGN', 'IBM', 'SBUX', 'LOW', 'RTX', 'INTU', 'GE', 'CAT', 'SPGI', 'GS', 'BLK', 'ISRG', 'PLD', 'AMT', 'LMT', 'NOW', 'DE', 'SYK', 'ZTS', 'CB', 'GILD', 'MO', 'MMC', 'ADI', 'MDLZ', 'CI', 'C', 'TGT', 'DUK', 'SO', 'PNC', 'USB', 'SCHW', 'CL', 'BDX', 'ICE', 'SHW', 'APD', 'ITW', 'EW', 'FIS', 'AON', 'VRTX', 'CSX', 'HUM', 'ETN', 'ADP', 'NSC', 'FDX', 'EMR', 'GM', 'PSA', 'ECL', 'AIG', 'AEP', 'COF', 'PSX', 'D', 'EXC', 'SRE', 'PEG', 'XEL', 'WEC', 'ES', 'ED', 'FE', 'EIX', 'DTE', 'CMS', 'LNT', 'NI', 'ATO', 'PNW', 'CNP', 'NRG', 'AES', 'OGE', 'EVRG', 'IDA', 'ALE', 'HE', 'PPL', 'SWX', 'UGI', 'SR', 'AVA', 'NWN', 'OTTR', 'BKH', 'CWT', 'SJW', 'AWR', 'MSEX', 'YORW'
  ];
  // ✅ State สำหรับจัดการคิวการเปิด Modal แก้ไขสัดส่วนหลายพอร์ตย่อย
  const [allocationQueue, setAllocationQueue] = useState<{ subPortId: string; subPortName: string; subPortObj: any }[]>([]);
  const [currentAllocationIndex, setCurrentAllocationIndex] = useState(0);
  // --- Derived Data ---
  const selectedPortfolio = useMemo(() => portfolios.find(p => (p.portfolioId || p.portId || p.id) === selectedPortId), [portfolios, selectedPortId]);
  const currentCash = selectedPortfolio?.summary?.cashValue ?? selectedPortfolio?.cashBalances?.[0]?.amount ?? 0;
  const subPorts = selectedPortfolio?.subPorts || [];
  const selectedSubPort = useMemo(() => subPorts.find((s: any) => s.subPortId === selectedSubPortId), [subPorts, selectedSubPortId]);
  const holdings = selectedSubPort?.holdings || selectedSubPort?.stocks || [];
  // หุ้นที่ยังถืออยู่จริง (ไม่ใช่ CLOSED และจำนวน > 0) — หุ้นที่เคยมีแต่ขายหมดไปแล้วยังค้างอยู่ใน holdings
  // ด้วยสถานะ CLOSED ถ้าซื้อกลับเข้ามาใหม่ต้องนับเป็น "หุ้นใหม่" (กรอบสีม่วง + เปิด popup แก้ไขสัดส่วน)
  const isActiveHolding = (h: any) => h?.status !== 'CLOSED' && Number(h?.quantity || 0) > 0;

  const [showSuccessSummary, setShowSuccessSummary] = useState(false);
  const [successSummaryData, setSuccessSummaryData] = useState<SummaryItem[]>([]);
  // ราคาตลาดปัจจุบันที่หน้าพอร์ต (PortfolioDetailPage) โหลดไว้ในแคชกลางอยู่แล้ว ใช้เป็น fallback
  // ตอนคำนวณ "สัดส่วนก่อน/หลัง" ของ popup สรุปผล เผื่อ fetch สดตอนปิด modal พลาดบาง symbol
  const { currentPrices: cachedMarketPrices } = useMarketPrices();

  const getSubPortName = (subPortId: string) => {
    const sub = subPorts.find((s: any) => s.subPortId === subPortId);
    return sub ? sub.name : '-';
  };
  const filteredHoldings = useMemo(() => {
    // ✅ กรองทั้งสถานะ CLOSED และหุ้นที่มีจำนวน (quantity) เป็น 0 ออก
    const activeHoldings = holdings.filter(isActiveHolding);

    const query = searchTerm.trim().toLowerCase();
    if (!query) return activeHoldings;

    return activeHoldings.filter((h: any) =>
      (h.symbol || h.stockSymbol || h.assetSymbol)?.toLowerCase().includes(query)
    );
  }, [holdings, searchTerm]);

  const currentHolding = useMemo(() => {
    if (!symbol) return null;
    return holdings.find((h: any) => (h.symbol || h.stockSymbol || h.assetSymbol)?.toUpperCase() === symbol.toUpperCase());
  }, [symbol, holdings]);


  // ✅ แก้ไข useEffect ตัวนี้ใหม่ทั้งหมด
  useEffect(() => {
    if (isOpen) {
      setIsEditingCash(false);
      setTransactions([]);

      // ดึงค่าตั้งต้นมาใส่ State
      setSelectedPortId(initialPortfolioId || '');
      setSelectedSubPortId(initialSubPortfolioId || '');
      setSymbol(initialSymbol || '');
      // setPrice(initialPrice ?? ''); // ✅ เปลี่ยนมาใช้ ?? ตรงนี้ครับ
      if (initialSymbol) {
        setType('SELL');
      } else {
        setType('BUY'); // ค่าเริ่มต้นปกติ
      }

      setPrice(''); // ✅ บังคับให้ราคาว่างเปล่าเสมอเพื่อให้ผู้ใช้กรอกเอง
      // ล้างค่าที่เหลือ
      setQuantity(initialQuantity ?? ''); setAmount(''); setFee(''); setTax('');
    }
  }, [isOpen, initialPortfolioId, initialSubPortfolioId, initialSymbol, initialPrice, initialQuantity, isSellAll]);

  useEffect(() => {
    if (selectedPortfolio && !subPorts.find((s: any) => s.subPortId === selectedSubPortId)) {
      setSelectedSubPortId(subPorts[0]?.subPortId || '');
    }
    setIsEditingCash(false);
  }, [selectedPortfolio, subPorts]);


  const handlePortChange = (newPortId: string) => {
    // เลือกพอร์ตคริปโต → สลับไป CryptoTradeModal (คิวหุ้นใช้กับพอร์ตคริปโตไม่ได้)
    if (onSwitchToCrypto && cryptoPortfolios.some(p => (p.portfolioId || p.portId || p.id) === newPortId)) {
      if (
        transactions.length > 0 &&
        !window.confirm(`คำเตือน: มีรายการที่รอตรวจสอบอยู่ ${transactions.length} รายการ
พอร์ตนี้เป็นพอร์ตคริปโต ระบบจะล้างรายการในคิวแล้วเปิดหน้าซื้อขายเหรียญแทน ต้องการไปต่อใช่หรือไม่?`)
      ) return;
      onSwitchToCrypto(newPortId);
      return;
    }

    // ถ้าพอร์ตใหม่เป็นค่าเดิม หรือไม่มีรายการค้างอยู่ ให้เปลี่ยนได้เลยทันที
    if (newPortId === selectedPortId || transactions.length === 0) {
      setSelectedPortId(newPortId);
      return;
    }

    // ถ้ามีรายการค้างอยู่ในคิว และพอร์ตเปลี่ยน ให้แสดง Pop-up เตือน User
    const confirmChange = window.confirm(
      `คำเตือน: มีรายการที่รอตรวจสอบอยู่ ${transactions.length} รายการ\nหากเปลี่ยนพอร์ต ระบบจะล้างรายการทั้งหมดออกจากคิว คุณต้องการเปลี่ยนพอร์ตใช่หรือไม่?`
    );

    if (confirmChange) {
      setTransactions([]); // เคลียร์คิวทิ้ง
      setSelectedPortId(newPortId); // เปลี่ยนพอร์ตใหม่
    }
  };

  // แยกกลุ่มพอร์ตหุ้น / พอร์ตคริปโต (ถ้าไม่มีพอร์ตคริปโต แสดงรายการเดิม ไม่มีหัวกลุ่ม)
  const renderPortfolioOptions = () => {
    const stockOptions = portfolios.map(p => <option style={{ color: 'var(--text-color)', fontWeight: 600 }} key={p.portfolioId || p.portId || p.id} value={p.portfolioId || p.portId || p.id}>{p.name}</option>);
    if (!onSwitchToCrypto || cryptoPortfolios.length === 0) return stockOptions;
    return (
      <>
        <optgroup style={{ color: 'var(--yellow-text-color)', fontWeight: 800 }} label="พอร์ตหุ้น">{stockOptions}</optgroup>
        <optgroup style={{ color: 'var(--yellow-text-color)', fontWeight: 800 }} label="พอร์ตคริปโต (เปิดหน้าซื้อขายเหรียญ)">
          {cryptoPortfolios.map(p => <option style={{ color: 'var(--text-color)', fontWeight: 600 }} key={p.portfolioId || p.portId || p.id} value={p.portfolioId || p.portId || p.id}>{p.name}</option>)}
        </optgroup>
      </>
    );
  };

  const fetchHotStocks = async () => {
    setLoadingSearch(true);
    const topSymbols = POPULAR_SYMBOLS.slice(0, 10);
    const promises = topSymbols.map(async (symbol) => {
      const price = await fetchPolygonSnapshotPrice(symbol);
      return { symbol, name: symbol, price };
    });
    const hot = (await Promise.all(promises)).filter(
      (item): item is { symbol: string; name: string; price: number } => item.price !== null
    );
    setSearchResults(hot);
    setLoadingSearch(false);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.trim().toUpperCase();
    setSearchQuery(value);
    setSymbol(value); // ตั้งค่า symbol โดยตรงเมื่อผู้ใช้พิมพ์

    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    if (value.length < 1) {
      setSearchResults([]);
      setShowDropdown(false);
      setLoadingSearch(false);
      return;
    }

    setLoadingSearch(true);

    const timeout = setTimeout(async () => {
      try {
        const res = await fetchUsStockSymbols(value);

        const withPrices = await Promise.all(
          res.map(async (item) => {
            const price = await fetchPolygonSnapshotPrice(item.symbol);
            return { ...item, price };
          })
        );

        const filteredResults = withPrices.filter((item) =>
          item.symbol.toLowerCase().includes(value.toLowerCase()) ||
          item.name.toLowerCase().includes(value.toLowerCase())
        );

        filteredResults.sort((a, b) => a.symbol.localeCompare(b.symbol));

        setSearchResults(filteredResults);
        setShowDropdown(filteredResults.length > 0);
      } catch (error) {
        console.error('Error fetching stock symbols or prices:', error);
        setSearchResults([]);
        setShowDropdown(false);
      } finally {
        setLoadingSearch(false);
      }
    }, 300); // Delay of 300ms

    setTypingTimeout(timeout);
  };

  const handleSearchFocus = async () => {
    if (searchQuery.length === 0 && hotStocks.length === 0) {
      await fetchHotStocks();
    }
    if (searchQuery.length === 0) {
      setShowDropdown(true);
    }
  };

  const handleSelectSymbol = (symbol: string) => {
    setSymbol(symbol);
    setSearchQuery('');
    setSearchResults([]);
    setShowDropdown(false);
    setIsForcedNewStock(false); // ✅ รีเซ็ตกลับ
  };

  const handleAddNewStockClick = () => {
    setSymbol('');
    setType('BUY');
    setPrice('');
    setQuantity('');
    setFee('');
    setTax('');
    setIsForcedNewStock(false); // ✅ รีเซ็ตกลับ
    setIsForcedNewStock(true); // ✅ บังคับให้เป็นสถานะหุ้นใหม่ทันทีเมื่อกดปุ่มนี้
    setIsAnimatingSymbol(true);
    setTimeout(() => setIsAnimatingSymbol(false), 500);
  };

  // ✅ เปลี่ยนจากคลิกทั้งแผง มาเป็นคลิกปุ่ม ซื้อ/ขาย
  const handleActionClick = (stockSymbol: string, actionType: TransactionType) => {
    setSymbol(stockSymbol);
    setType(actionType);
    setIsAnimatingSymbol(true);
    setTimeout(() => setIsAnimatingSymbol(false), 500);
  };

  const handleSaveCash = async () => {
    if (!selectedPortId || !onAdjustCash) return;
    const newAmount = parseFloat(cashInput);
    if (isNaN(newAmount)) { toast.error('กรุณาระบุตัวเลขให้ถูกต้อง'); return; }

    const difference = newAmount - currentCash;
    if (difference === 0) { setIsEditingCash(false); return; }

    setIsAdjustingCash(true);
    try {
      await onAdjustCash(selectedPortId, difference, new Date().toISOString());
      setIsEditingCash(false);
      if (onRefresh) onRefresh(); // ✅ เรียกสั่งรีเฟรชข้อมูลจาก Parent
    } catch (error) { console.error(error); }
    finally { setIsAdjustingCash(false); }
  };

  // ✅ ถ้าเลือก/พิมพ์วันที่เกินวันนี้ ให้ปัดกลับเป็นวันนี้
  const handleDateChange = (value: string) => {
    const today = getTodayStr();
    if (value && value > today) {
      toast.error('ไม่สามารถระบุวันที่ในอนาคตได้');
      setDate(today);
      return;
    }
    setDate(value);
  };

  const handleAddTransaction = () => {

    // ✅ เงื่อนไขจำกัดรายการไม่เกิน 20 รายการ
    if (transactions.length >= 20) {
      toast.error('สามารถเพิ่มรายการได้สูงสุด 20 รายการเท่านั้น');
      return;
    }

    // ✅ ห้ามใส่วันที่ในอนาคต (ย้อนหลังได้)
    if (!date) {
      toast.error('กรุณาระบุวันที่'); return;
    }
    if (date > getTodayStr()) {
      toast.error('ไม่สามารถระบุวันที่ในอนาคตได้'); return;
    }

    if (!selectedPortId || (!selectedSubPortId && type !== 'CASH_DEPOSIT' && type !== 'CASH_WITHDRAW')) {
      toast.error('กรุณาเลือกพอร์ตและพอร์ตย่อย'); return;
    }
    if (!symbol && !['CASH_DEPOSIT', 'CASH_WITHDRAW'].includes(type)) {
      toast.error('กรุณาระบุ Symbol'); return;
    }

    const parsedQty = Number(quantity) || 0;
    const parsedPrice = Number(price) || 0;
    const parsedAmount = Number(amount) || 0;
    const parsedFee = Number(fee) || 0;
    const parsedTax = Number(tax) || 0;

    if (!['CASH_DEPOSIT', 'CASH_WITHDRAW'].includes(type)) {
      if (parsedQty <= 0) {
        toast.error('กรุณากรอกจำนวนหุ้นให้มากกว่า 0');
        return;
      }
      if (parsedPrice <= 0) {
        toast.error('กรุณากรอกราคาให้มากกว่า 0');
        return;
      }
    }

    // ✅ ตรวจสอบว่าจำนวนหุ้นที่จะขาย ไม่เกินจำนวนที่มีอยู่จริง (นับรวมรายการขาย/ซื้อที่อยู่ในคิวของ symbol/พอร์ตย่อยนี้ด้วย)
    if (type === 'SELL') {
      const upperSymForCheck = symbol.trim().toUpperCase();
      const existingHolding = holdings.find((h: any) =>
        (h.symbol || h.stockSymbol || h.assetSymbol)?.toUpperCase() === upperSymForCheck
      );
      const baseQty = Number(existingHolding?.quantity || 0);
      const queuedDelta = transactions.reduce((acc, t) => {
        if (t.subPortId !== selectedSubPortId) return acc;
        if ((t.symbol || '').toUpperCase() !== upperSymForCheck) return acc;
        const q = t.quantity || 0;
        if (t.type === 'BUY' || t.type === 'OPENING_POSITION') return acc + q;
        if (t.type === 'SELL') return acc - q;
        return acc;
      }, 0);
      const availableQty = baseQty + queuedDelta;

      if (parsedQty > availableQty + 0.000001) {
        toast.error(`จำนวนหุ้นที่ขายเกินกว่าที่มีอยู่ (คงเหลือ: ${availableQty.toLocaleString(undefined, { maximumFractionDigits: 6 })} หุ้น)`);
        return;
      }
    }

    // 1. คำนวณเงินสดคงเหลือจากรายการก่อนหน้าในคิว
    let currentBalance = currentCash;
    transactions.forEach(t => {
      const q = t.quantity || 0;
      const p = t.price || 0;
      const f = t.fee || 0;
      const tax = t.tax || 0;
      const amt = t.amount || 0;

      if (t.type === 'BUY') currentBalance -= (q * p + f + tax);
      else if (t.type === 'SELL') currentBalance += (q * p - f - tax);
      else if (t.type === 'CASH_WITHDRAW') currentBalance -= amt;
      else if (t.type === 'CASH_DEPOSIT') currentBalance += amt;
      else if (t.type === 'DIVIDEND' || t.type === 'PROFIT_INCOME') currentBalance += (amt - tax);
    });

    // 2. คำนวณผลกระทบของรายการใหม่ (newImpact)
    let newImpact = 0;
    if (type === 'BUY') newImpact = -(parsedQty * parsedPrice + parsedFee + parsedTax);
    else if (type === 'SELL') newImpact = (parsedQty * parsedPrice - parsedFee - parsedTax);
    else if (type === 'CASH_WITHDRAW') newImpact = -parsedAmount;
    else if (type === 'CASH_DEPOSIT') newImpact = parsedAmount;
    else if (type === 'DIVIDEND' || type === 'PROFIT_INCOME') newImpact = (parsedAmount - parsedTax);

    const allowNegative = selectedPortfolio?.settings?.allowNegativeCash === true;

    if (!allowNegative && (currentBalance + newImpact) < -0.0001) {
      toast.error(`ยอดเงินสดไม่เพียงพอ! (คงเหลือ: $${currentBalance.toFixed(2)})`);
      return;
    }

    // ✅ ตรวจสอบว่าเป็นหุ้นตัวใหม่ที่ไม่มีอยู่ในพอร์ตย่อยนี้หรือไม่
    const upperSym = symbol.toUpperCase();
    const isExistingInPortfolio = holdings.some((h: any) =>
      isActiveHolding(h) && (h.symbol || h.stockSymbol || h.assetSymbol)?.toUpperCase() === upperSym
    );
    // ถ้าระบุ Symbol และไม่มีอยู่ในพอร์ตเดิม (หรือกดปุ่มเพิ่มหุ้นใหม่ / พิมพ์ชื่อใหม่) ให้ถือว่าเป็นหุ้นใหม่
    const isNewStock = Boolean(upperSym && !isExistingInPortfolio);
    const currentPort = portfolios.find(p => (p.portfolioId || p.portId || p.id) === selectedPortId);
    const currentSubPort = currentPort?.subPorts?.find((s: any) => s.subPortId === selectedSubPortId) ||
      currentPort?.subPortfolios?.find((s: any) => s.subPortId === selectedSubPortId);
    const newItem: TransactionItem = {
      id: Math.random().toString(36).substring(7),
      date: new Date(date).toISOString(),
      type,
      symbol: upperSym,
      quantity: parsedQty > 0 ? parsedQty : undefined,
      price: parsedPrice > 0 ? parsedPrice : undefined,
      amount: parsedAmount > 0 ? parsedAmount : undefined,
      subPortId: selectedSubPortId,
      fee: parsedFee,
      tax: parsedTax,
      idempotencyKey: `txn-${Date.now()}-${Math.random()}`,
      isNewStock, // ✅ บันทึกสถานะหุ้นใหม่
      portfolioName: currentPort?.name || '',       // ✅ บันทึกชื่อพอร์ตหลัก
      subPortName: currentSubPort?.name || '-'
    };

    setTransactions(prev => [...prev, newItem]);
    setQuantity(''); setPrice(''); setAmount(''); setFee(''); setTax('');
  };

  const handleEditTransaction = (t: TransactionItem) => {
    setType(t.type);
    setSymbol(t.symbol);
    // แปลงวันที่ให้เข้ากับ input type="date" (YYYY-MM-DD)
    if (t.date) {
      setDate(t.date.split('T')[0]);
    }
    setQuantity(t.quantity ?? '');
    setPrice(t.price ?? '');
    setAmount(t.amount ?? '');
    setFee(t.fee ?? '');
    setTax(t.tax ?? '');

    // ลบรายการนั้นออกจากคิวก่อน เพื่อรอเพิ่มใหม่หลังแก้เสร็จ
    setTransactions(prev => prev.filter(item => item.id !== t.id));

    // ทำ Animation กระพริบที่ฟอร์มฝั่งขวาเพื่อให้ผู้รู้ว่าข้อมูลถูกดึงมาแล้ว
    setIsAnimatingSymbol(true);
    setTimeout(() => setIsAnimatingSymbol(false), 500);
  };



  const handleRemoveTransaction = (id: string) => setTransactions(prev => prev.filter(t => t.id !== id));
  const handleSubmitAll = async () => {
    if (transactions.length === 0) return;

    // --- ตรวจสอบเงินสดก่อนส่งคำสั่งจริง ---
    let projectedCash = currentCash;
    transactions.forEach(t => {
      const qty = t.quantity || 0;
      const priceVal = t.price || 0;
      const feeVal = t.fee || 0;
      const taxVal = t.tax || 0;
      const amt = t.amount || 0;

      if (t.type === 'BUY') {
        projectedCash -= (qty * priceVal + feeVal + taxVal);
      } else if (t.type === 'SELL') {
        projectedCash += (qty * priceVal - feeVal - taxVal);
      } else if (t.type === 'CASH_WITHDRAW') {
        projectedCash -= amt;
      } else if (t.type === 'CASH_DEPOSIT') {
        projectedCash += amt;
      } else if (t.type === 'DIVIDEND' || t.type === 'PROFIT_INCOME') {
        projectedCash += (amt - taxVal);
      }
    });

    if (projectedCash < 0 && selectedPortfolio?.settings?.allowNegativeCash === false) {
      toast.error(`ยอดเงินสดไม่เพียงพอ! เงินสดปัจจุบัน: $${currentCash.toFixed(2)}, ยอดที่ต้องใช้เพิ่ม: $${Math.abs(projectedCash).toFixed(2)}`);
      return;
    }

    // ✅ 1. Snapshot ข้อมูล "ก่อนทำรายการ" จาก state ปัจจุบัน (ของจริง ไม่ใช่ค่าจำลอง) ก่อนยิง API
    const oldSnapshotMap = new Map<string, { oldQty: number; oldAvgPrice: number }>();
    // เก็บ holdings ดิบของแต่ละพอร์ตย่อยไว้ก่อนทำรายการ เพื่อเอาไปคำนวณ "มูลค่าตลาดรวม" (ไม่ใช่ต้นทุน)
    // ของพอร์ตย่อยนั้นในขั้นถัดไป — ให้สูตรสัดส่วนตรงกับ PortfolioStocksBox.tsx (currentRatio = stockValue / subPortfolioValue)
    const subPortHoldingsBeforeMap = new Map<string, any[]>();
    {
      const portBefore = portfolios.find(p => (p.portfolioId || p.portId || p.id) === selectedPortId);
      const subPortsBefore = portBefore?.subPorts || portBefore?.subPortfolios || [];

      const involvedSubPortIdsForSnapshot = Array.from(new Set(transactions.map(t => t.subPortId)));
      involvedSubPortIdsForSnapshot.forEach(subPortId => {
        const targetSubPort = subPortsBefore.find((s: any) => s.subPortId === subPortId);
        subPortHoldingsBeforeMap.set(subPortId, targetSubPort?.holdings || targetSubPort?.stocks || []);
      });

      transactions.forEach(t => {
        if (!['BUY', 'SELL', 'OPENING_POSITION'].includes(t.type)) return;
        const sym = t.symbol.toUpperCase();
        const key = `${t.subPortId}_${sym}`;
        if (oldSnapshotMap.has(key)) return;

        const targetSubPort = subPortsBefore.find((s: any) => s.subPortId === t.subPortId);
        const subPortHoldings = targetSubPort?.holdings || targetSubPort?.stocks || [];
        const existing = subPortHoldings.find((h: any) => (h.assetSymbol || h.stockSymbol || h.symbol)?.toUpperCase() === sym);
        oldSnapshotMap.set(key, {
          oldQty: Number(existing?.quantity || 0),
          oldAvgPrice: Number(existing?.avgEntryPrice ?? existing?.avgBuyPrice ?? 0)
        });
      });
    }

    // ✅ รวมยอดขาย (qty + proceeds สุทธิหลังหักค่าธรรมเนียม/ภาษี) ต่อ symbol/พอร์ตย่อย เพื่อคำนวณกำไร/ขาดทุนจากการขายครั้งนี้
    // ใช้ oldAvgPrice (WAC ก่อนขาย) เป็นต้นทุนของหุ้นที่ขาย เพราะ WAC ไม่เปลี่ยนระหว่างขายล็อตเดียวกันหลายรายการ
    const realizedSellMap = new Map<string, { sellQty: number; proceeds: number }>();
    transactions.forEach(t => {
      if (t.type !== 'SELL') return;
      const sym = t.symbol.toUpperCase();
      const key = `${t.subPortId}_${sym}`;
      const qty = t.quantity || 0;
      const prc = t.price || 0;
      const feeVal = t.fee || 0;
      const taxVal = t.tax || 0;
      const proceeds = qty * prc - feeVal - taxVal;
      const prevEntry = realizedSellMap.get(key) || { sellQty: 0, proceeds: 0 };
      realizedSellMap.set(key, { sellQty: prevEntry.sellQty + qty, proceeds: prevEntry.proceeds + proceeds });
    });

    setIsSubmitting(true);
    try {
      // 2. ส่งข้อมูลบันทึกรายการทั้งหมด
      await onConfirm(selectedPortId, selectedSubPortId, transactions);

      // ✅ รอให้ backend คำนวณ/อัปเดตข้อมูลพอร์ตและหุ้นใหม่ให้เสร็จก่อน
      // (กันปัญหาข้อมูลหุ้นใหม่ยังอัพเดตไม่ทันตอนดึงข้อมูลมาโชว์ใน Popup)
      await new Promise(resolve => setTimeout(resolve, 3000));

      // ✅ 3. ดึงข้อมูลพอร์ตล่าสุด "จริง" จาก backend อีกรอบ (ถ้า onRefresh คืน array มาให้ ใช้ตัวนั้นก่อน
      // เพราะ prop `portfolios` ในฟังก์ชันนี้ยัง capture ค่าเก่าค้างอยู่จนกว่า component จะ re-render)
      let refreshedPortfolios: any[] | null = null;
      if (onRefresh) {
        const result = await onRefresh();
        if (Array.isArray(result)) refreshedPortfolios = result;
      }

      const sourcePortfolios = refreshedPortfolios || portfolios;
      const currentPort = sourcePortfolios.find(p => (p.portfolioId || p.portId || p.id) === selectedPortId);
      const allSubPorts = currentPort?.subPorts || currentPort?.subPortfolios || [];

      // ✅ ดึง "ราคาตลาดปัจจุบัน" ของทุก symbol ในพอร์ตย่อยที่เกี่ยวข้อง (ทั้งชุดก่อน/หลังทำรายการ)
      // เพื่อคำนวณ "สัดส่วน" ด้วยมูลค่าตลาด (qty × ราคาปัจจุบัน) แบบเดียวกับ PortfolioStocksBox.tsx
      // (currentRatio = stockValue / subPortfolioValue) แทนการใช้ฐานต้นทุนแบบเดิม
      const involvedSubPortIdsAfter = Array.from(new Set(transactions.map(t => t.subPortId)));
      const getSymbolFromHolding = (h: any) => (h.assetSymbol || h.stockSymbol || h.symbol || '').toUpperCase();

      const allSymbolsForPricing = new Set<string>();
      involvedSubPortIdsAfter.forEach(subPortId => {
        (subPortHoldingsBeforeMap.get(subPortId) || []).forEach((h: any) => {
          const s = getSymbolFromHolding(h);
          if (s) allSymbolsForPricing.add(s);
        });
        const targetSubPortAfter = allSubPorts.find((s: any) => s.subPortId === subPortId);
        const subPortHoldingsAfter = targetSubPortAfter?.holdings || targetSubPortAfter?.stocks || [];
        subPortHoldingsAfter.forEach((h: any) => {
          const s = getSymbolFromHolding(h);
          if (s) allSymbolsForPricing.add(s);
        });
      });

      const currentPriceMap = new Map<string, number>();
      // ใช้ราคาที่แคชไว้ในหน้าพอร์ต (MarketPriceContext) เป็นฐานตั้งต้นก่อน — เป็นราคาชุดเดียวกับที่
      // PortfolioStocksBox.tsx ใช้แสดง "สัดส่วน" อยู่แล้ว กันกรณี fetch สดด้านล่างพลาดบาง symbol (เช่น OKLO)
      allSymbolsForPricing.forEach(sym => {
        const cached = cachedMarketPrices[sym];
        if (cached) currentPriceMap.set(sym, Number(cached));
      });
      try {
        const freshPrices = await fetchPolygonSnapshotPriceses(Array.from(allSymbolsForPricing));
        Object.entries(freshPrices).forEach(([sym, price]) => {
          if (price) currentPriceMap.set(sym.toUpperCase(), Number(price));
        });
      } catch {
        // ดึงราคาสดไม่ได้ -> ใช้ราคาที่แคชไว้ข้างบน (ถ้ามี) หรือ fallback เป็นต้นทุนเฉลี่ยของแต่ละ holding ด้านล่าง
      }
      const getPriceOrAvgFallback = (h: any) => {
        const sym = getSymbolFromHolding(h);
        const avgPrice = Number(h.avgEntryPrice ?? h.avgBuyPrice ?? 0);
        return currentPriceMap.get(sym) ?? avgPrice;
      };

      // มูลค่าตลาดรวมของแต่ละพอร์ตย่อย "ก่อน" ทำรายการ (ใช้ราคาปัจจุบันตัวเดียวกันตีทั้งก่อน/หลัง
      // เพราะราคาตลาดไม่เปลี่ยนระหว่าง flow นี้ มีแค่ qty/avgPrice ที่เปลี่ยน)
      const subPortTotalValueBeforeMap = new Map<string, number>();
      involvedSubPortIdsAfter.forEach(subPortId => {
        const subPortHoldings = subPortHoldingsBeforeMap.get(subPortId) || [];
        const totalValue = subPortHoldings.reduce((sum: number, h: any) => {
          const qty = Number(h.quantity || 0);
          return sum + qty * getPriceOrAvgFallback(h);
        }, 0);
        subPortTotalValueBeforeMap.set(subPortId, totalValue);
      });

      // มูลค่าตลาดรวมของแต่ละพอร์ตย่อย "หลัง" ทำรายการ
      const subPortTotalValueAfterMap = new Map<string, number>();
      involvedSubPortIdsAfter.forEach(subPortId => {
        const targetSubPort = allSubPorts.find((s: any) => s.subPortId === subPortId);
        const subPortHoldings = targetSubPort?.holdings || targetSubPort?.stocks || [];
        const totalValue = subPortHoldings.reduce((sum: number, h: any) => {
          const qty = Number(h.quantity || 0);
          return sum + qty * getPriceOrAvgFallback(h);
        }, 0);
        subPortTotalValueAfterMap.set(subPortId, totalValue);
      });

      // -------------------------------------------------------------
      // 🌟 4. สร้างสรุปการเปลี่ยนแปลง จากข้อมูลจริงที่ fetch มาล้วนๆ (ไม่คำนวณจำลอง)
      // -------------------------------------------------------------
      const finalSummary: SummaryItem[] = [];
      const seenKeys = new Set<string>();

      transactions.forEach(t => {
        if (!['BUY', 'SELL', 'OPENING_POSITION'].includes(t.type)) return;

        const sym = t.symbol.toUpperCase();
        const key = `${t.subPortId}_${sym}`;
        if (seenKeys.has(key)) return;
        seenKeys.add(key);

        const targetSubPort = allSubPorts.find((s: any) => s.subPortId === t.subPortId);
        const subPortHoldings = targetSubPort?.holdings || targetSubPort?.stocks || [];
        const existing = subPortHoldings.find((h: any) => (h.assetSymbol || h.stockSymbol || h.symbol)?.toUpperCase() === sym);

        const oldSnap = oldSnapshotMap.get(key) || { oldQty: 0, oldAvgPrice: 0 };
        const newQty = Number(existing?.quantity || 0);
        const newAvgPrice = Number(existing?.avgEntryPrice ?? existing?.avgBuyPrice ?? 0);

        const symPrice = currentPriceMap.get(sym) ?? newAvgPrice ?? oldSnap.oldAvgPrice ?? 0;
        const totalValueBefore = subPortTotalValueBeforeMap.get(t.subPortId) || 0;
        const totalValueAfter = subPortTotalValueAfterMap.get(t.subPortId) || 0;
        const oldRatio = totalValueBefore > 0 ? ((oldSnap.oldQty * symPrice) / totalValueBefore) * 100 : 0;
        const newRatio = totalValueAfter > 0 ? ((newQty * symPrice) / totalValueAfter) * 100 : 0;

        const hasSell = transactions.some(tx => tx.subPortId === t.subPortId && tx.symbol.toUpperCase() === sym && tx.type === 'SELL');
        const hasBuy = transactions.some(tx => tx.subPortId === t.subPortId && tx.symbol.toUpperCase() === sym && (tx.type === 'BUY' || tx.type === 'OPENING_POSITION'));

        let realizedGain: number | null = null;
        let realizedGainPercent: number | null = null;
        const realizedEntry = realizedSellMap.get(key);
        if (realizedEntry) {
          const costOfSold = realizedEntry.sellQty * oldSnap.oldAvgPrice;
          realizedGain = realizedEntry.proceeds - costOfSold;
          realizedGainPercent = costOfSold > 0 ? (realizedGain / costOfSold) * 100 : null;
        }

        finalSummary.push({
          symbol: sym,
          oldQty: oldSnap.oldQty,
          oldAvgPrice: oldSnap.oldAvgPrice,
          newQty,
          newAvgPrice,
          oldRatio,
          newRatio,
          hasBuy,
          hasSell,
          realizedGain,
          realizedGainPercent,
        });
      });

      setSuccessSummaryData(finalSummary); // เก็บข้อมูลรอโชว์

      // -------------------------------------------------------------
      // 🌟 3. ตรวจสอบว่าต้องเปิด Popup แก้ไขสัดส่วนหรือไม่
      // -------------------------------------------------------------
      const uniqueSubPortsMap = new Map();

      const deltaMap = new Map<string, Map<string, number>>();
      transactions.forEach(t => {
        if (!['BUY', 'SELL'].includes(t.type)) return;
        if (!deltaMap.has(t.subPortId)) deltaMap.set(t.subPortId, new Map());

        const subMap = deltaMap.get(t.subPortId)!;
        const upperSym = t.symbol.toUpperCase();
        const currentDelta = subMap.get(upperSym) || 0;
        const qty = t.quantity || 0;

        subMap.set(upperSym, t.type === 'BUY' ? currentDelta + qty : currentDelta - qty);
      });

      const involvedSubPortIds = Array.from(new Set(transactions.map(t => t.subPortId)));

      involvedSubPortIds.forEach(subPortId => {
        let targetSubPort = allSubPorts.find((s: any) => s.subPortId === subPortId);
        if (!targetSubPort) return;

        let subPortHoldings = [...(targetSubPort.holdings || targetSubPort.stocks || [])];
        let triggerAllocation = false;

        const newStockTxns = transactions.filter(t => t.subPortId === subPortId && t.isNewStock);
        if (newStockTxns.length > 0) {
          triggerAllocation = true;
          newStockTxns.forEach(t => {
            const existingIndex = subPortHoldings.findIndex((h: any) =>
              (h.assetSymbol || h.stockSymbol || h.symbol)?.toUpperCase() === t.symbol.toUpperCase()
            );
            if (existingIndex < 0) {
              subPortHoldings.push({ symbol: t.symbol, assetSymbol: t.symbol, quantity: t.quantity, expectedRatio: 0 });
            } else if (!isActiveHolding(subPortHoldings[existingIndex])) {
              // หุ้นที่เคย CLOSED แล้วซื้อกลับเข้ามาใหม่ — ถ้า backend ยังอัปเดตสถานะไม่ทัน ให้ถือว่าเปิดสถานะแล้ว
              // ไม่งั้นติ๊ก "ซ่อนหุ้นที่ปิดสถานะแล้ว" ใน popup แก้ไขสัดส่วนจะซ่อนหุ้นตัวที่เพิ่งซื้อไป
              const { value: _staleValue, ...rest } = subPortHoldings[existingIndex];
              subPortHoldings[existingIndex] = { ...rest, status: 'OPEN', quantity: t.quantity };
            }
          });
        }

        if (deltaMap.has(subPortId)) {
          const subMap = deltaMap.get(subPortId)!;
          subMap.forEach((deltaQty, symbol) => {
            // ต้องใช้จำนวนหุ้น "ก่อนทำรายการ" จาก oldSnapshotMap — allSubPorts ตรงนี้เป็นข้อมูลที่ refresh มาหลัง
            // ยิง API แล้ว (หุ้นที่ขายหมดเหลือ 0 หรือหายไปแล้ว) ถ้าเอา qty จาก allSubPorts มาเป็น oldQty จะได้ 0
            // เสมอ เลยไม่เคยเข้าเงื่อนไข "ขายหมด" และไม่เปิด popup แก้ไขสัดส่วน
            const oldQty = oldSnapshotMap.get(`${subPortId}_${symbol}`)?.oldQty ?? 0;
            const newQty = oldQty + deltaQty;
            if (oldQty > 0.000001 && newQty <= 0.000001) {
              triggerAllocation = true;
              // 🌟 หุ้นที่ขายหมดในรายการนี้: ตั้งสัดส่วนเป้าหมายเป็น 0% ไว้ก่อนเปิด popup แก้ไขสัดส่วน
              // และ mark เป็น CLOSED/มูลค่า 0 (เผื่อ backend ยังอัปเดตไม่ทัน) เพื่อให้ติ๊ก "ซ่อนหุ้นที่ปิดสถานะแล้ว" ซ่อนได้
              const existingIndex = subPortHoldings.findIndex((h: any) => (h.assetSymbol || h.stockSymbol || h.symbol)?.toUpperCase() === symbol);
              if (existingIndex >= 0) {
                subPortHoldings[existingIndex] = {
                  ...subPortHoldings[existingIndex],
                  quantity: 0,
                  value: 0,
                  status: 'CLOSED',
                  expectedRatio: 0,
                };
              }
            }
          });
        }

        if (triggerAllocation) {
          targetSubPort = { ...targetSubPort, holdings: subPortHoldings, stocks: subPortHoldings };
          uniqueSubPortsMap.set(subPortId, {
            subPortId: subPortId,
            subPortName: targetSubPort.name || '-',
            subPortObj: targetSubPort
          });
        }
      });

      const queueList = Array.from(uniqueSubPortsMap.values());
      if (queueList.length > 0) {
        setAllocationQueue(queueList);
        setCurrentAllocationIndex(0);
        setIsAllocationModalOpen(true);
        setIsSubmitting(false);
        return; // ออกไปเปิด Modal สัดส่วนก่อน ยังไม่เปิดหน้าต่างยินดีด้วย
      }

      // ถ้าไม่ต้องเปิด Modal สัดส่วน ให้ล้างคิวและเปิด Popup ยินดีด้วยเลย
      setTransactions([]);
      toast.success('ทำรายการสำเร็จ');
      setShowSuccessSummary(true);

    } catch (error) {
      console.error(error);
      showApiErrorToast(error, 'เกิดข้อผิดพลาดในการทำรายการ');
    } finally {
      setIsSubmitting(false);
    }
  };
  // --- คำนวณยอดเงินสดคงเหลือเฉพาะของพอร์ตหลักปัจจุบัน (โดยคร่าว) ---
  const projectedCash = useMemo(() => {
    let balance = currentCash;

    // ✅ กรองเฉพาะรายการที่อยู่ในพอร์ตหลัก (portfolioId) เดียวกัน ไม่สนว่าจะอยู่พอร์ตย่อยไหน
    const currentPortTransactions = transactions.filter(t => {
      // เนื่องจากใน TransactionItem เราเก็บ subPortId ไว้ เราต้องเช็คว่า subPortId นั้นอยู่ในพอร์ตหลักปัจจุบันไหม
      const targetSubPort = subPorts.find((sub: any) => sub.subPortId === t.subPortId);
      return targetSubPort !== undefined;
    });

    currentPortTransactions.forEach(t => {
      const qty = t.quantity || 0;
      const prc = t.price || 0;
      const feeVal = t.fee || 0;
      const taxVal = t.tax || 0;
      const amt = t.amount || 0;
      const totalVal = qty * prc;
      const net = t.type === 'BUY' ? totalVal + feeVal + taxVal : totalVal - feeVal - taxVal;

      if (t.type === 'BUY') {
        balance -= net;
      } else if (t.type === 'SELL') {
        balance += net;
      } else if (t.type === 'CASH_WITHDRAW') {
        balance -= amt;
      } else if (t.type === 'CASH_DEPOSIT') {
        balance += amt;
      } else if (t.type === 'DIVIDEND' || t.type === 'PROFIT_INCOME') {
        balance += (amt - taxVal);
      }
    });

    return balance;
  }, [currentCash, transactions, subPorts]);


  // 📊 คำนวณจำลองสถานะหุ้นหลังทำรายการในฟิลด์ปัจจุบัน + คิวที่รออยู่
  const simulatedHolding = useMemo(() => {
    if (!symbol || type === 'CASH_DEPOSIT' || type === 'CASH_WITHDRAW') return null;

    const upperSym = symbol.toUpperCase();
    const existing = holdings.find((h: any) => (h.symbol || h.stockSymbol || h.assetSymbol)?.toUpperCase() === upperSym);

    let baseQty = Number(existing?.quantity || 0);
    let baseAvgPrice = Number(existing?.avgEntryPrice ?? existing?.avgBuyPrice ?? 0);
    let totalCost = baseQty * baseAvgPrice; // คำนวณเป็นมูลค่าต้นทุนรวมตั้งต้น

    // รวบรวมรายการทั้งหมด (รายการในคิวปัจจุบัน + ฟอร์มที่กำลังกรอกอยู่)
    const activeQueue = [...transactions];
    const inputQty = Number(quantity) || 0;
    const inputPrice = Number(price) || 0;

    if (inputQty > 0 && symbol) {
      activeQueue.push({
        id: 'temp-input',
        date: date,
        type: type,
        symbol: upperSym,
        quantity: inputQty,
        price: inputPrice,
        fee: Number(fee) || 0,
        tax: Number(tax) || 0,
        subPortId: selectedSubPortId,
        idempotencyKey: 'temp'
      });
    }

    // กรองเฉพาะรายการที่ตรงกับ Symbol นี้ และอยู่ในพอร์ตย่อยนี้
    const targetTxns = activeQueue.filter(t => t.symbol?.toUpperCase() === upperSym && t.subPortId === selectedSubPortId);

    targetTxns.forEach(t => {
      const q = Number(t.quantity || 0);
      const p = Number(t.price || 0);

      if (t.type === 'BUY' || t.type === 'OPENING_POSITION') {
        totalCost += (q * p);
        baseQty += q;
      } else if (t.type === 'SELL') {
        if (baseQty > 0) {
          const reducedQty = Math.min(q, baseQty);
          // คำนวณราคาเฉลี่ยปัจจุบันก่อนตัดขาย
          const currentAvg = baseQty > 0 ? totalCost / baseQty : 0;
          totalCost -= (reducedQty * currentAvg);
          baseQty -= reducedQty;
          if (baseQty <= 0) {
            totalCost = 0;
            baseQty = 0;
          }
        }
      }
    });

    const finalAvgPrice = baseQty > 0 ? totalCost / baseQty : 0;
    return {
      quantity: baseQty,
      avgPrice: finalAvgPrice,
      hasChange: baseQty !== Number(existing?.quantity || 0) || finalAvgPrice !== baseAvgPrice
    };
  }, [symbol, type, quantity, price, fee, tax, transactions, holdings, selectedSubPortId, date]);
  // ✅ นับจำนวนรายการเฉพาะในพอร์ตหลักนี้ เพื่อใช้เช็คตอนแสดงผลบรรทัดยอดคงเหลือ
  const currentPortQueueCount = useMemo(() => {
    return transactions.filter(t => {
      const targetSubPort = subPorts.find((sub: any) => sub.subPortId === t.subPortId);
      return targetSubPort !== undefined;
    }).length;
  }, [transactions, subPorts]);

  // และเปลี่ยนเงื่อนไขตอนแสดงผล ให้เช็คเฉพาะคิวของพอร์ตย่อยนี้ด้วย (เพื่อให้บรรทัดยอดคงเหลือโชว์เฉพาะตอนที่มีรายการในพอร์ตนี้)
  const currentSubPortQueueCount = transactions.filter(t => t.subPortId === selectedSubPortId).length;


  // --- คำนวณสรุปยอดซื้อ/ขายในคิว ---
  const summaryTotals = useMemo(() => {
    let totalBuy = 0;
    let totalSell = 0;

    transactions.forEach(t => {
      const qty = t.quantity || 0;
      const prc = t.price || 0;
      const fee = t.fee || 0;
      const tax = t.tax || 0;
      const totalValue = qty * prc;

      // คำนวณ Net Amount ของแต่ละรายการตามสูตรเดียวกับในตาราง
      const netAmount = t.type === 'BUY' ? totalValue + fee + tax : totalValue - fee - tax;

      if (t.type === 'BUY') {
        totalBuy += netAmount;
      } else if (t.type === 'SELL') {
        totalSell += netAmount;
      }
    });

    return { totalBuy, totalSell };
  }, [transactions]);
  const isSellingAll = type === 'SELL' && currentHolding && Number(quantity) === currentHolding.quantity && currentHolding.quantity > 0;
  const upperInputSymbol = symbol.trim().toUpperCase();
  const isSymbolExisting = holdings.some((h: any) =>
    isActiveHolding(h) && (h.symbol || h.stockSymbol || h.assetSymbol)?.toUpperCase() === upperInputSymbol
  );
  const isNewStockForm = Boolean(isForcedNewStock || (upperInputSymbol && !isSymbolExisting));

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-0 md:p-4 bg-black/80 md:bg-black/70 backdrop-blur-sm animate-fadeIn">

      <div
        className="w-full h-full md:h-[95vh] md:max-w-7xl bg-[var(--bg-chart)] md:rounded-2xl shadow-2xl overflow-hidden text-[var(--text-color)] md:border border-[var(--border-item-light)] relative flex flex-col"
      >
        {isManageCashOpen && (
          <ManageCashModal
            isOpen={isManageCashOpen}
            onClose={() => setIsManageCashOpen(false)}
            onSave={() => {
              setIsManageCashOpen(false);
              if (onRefresh) onRefresh(); // ✅ เรียกสั่งรีเฟรชข้อมูลจาก Parent
            }}
            portfolioId={selectedPortId}
            currentCash={currentCash}
          />
        )}

        {/* ✅ แสดง Modal จัดการสัดส่วนพอร์ตย่อยแบบวนคิวหลายพอร์ตย่อย */}
        {/* ✅ แสดง Modal จัดการสัดส่วนพอร์ตย่อยแบบวนคิวหลายพอร์ตย่อย */}
        {isAllocationModalOpen && allocationQueue.length > 0 && allocationQueue[currentAllocationIndex] && (
          <EditHoldingAllocationModal
            isOpen={isAllocationModalOpen}
            onClose={() => {
              if (currentAllocationIndex < allocationQueue.length - 1) {
                setCurrentAllocationIndex(prev => prev + 1);
              } else {
                setIsAllocationModalOpen(false);
                setAllocationQueue([]);
                setCurrentAllocationIndex(0);
                setTransactions([]);
                setShowSuccessSummary(true); // 🌟 เปิดสรุปหลังจากกดกากบาทปิด Modal สัดส่วนแล้ว
              }
            }}
            portfolioId={selectedPortId}
            subPortfolio={allocationQueue[currentAllocationIndex].subPortObj}
            queueInfo={{
              currentIndex: currentAllocationIndex + 1,
              total: allocationQueue.length
            }}
            openedFromAction="TRADE"
            onSave={() => {
              if (currentAllocationIndex < allocationQueue.length - 1) {
                setCurrentAllocationIndex(prev => prev + 1);
              } else {
                setIsAllocationModalOpen(false);
                setAllocationQueue([]);
                setCurrentAllocationIndex(0);
                setTransactions([]);
                if (onRefresh) onRefresh();
                toast.success('ทำรายการและตั้งค่าสัดส่วนครบทุกพอร์ตย่อยแล้ว');
                setShowSuccessSummary(true); // 🌟 เปิดสรุปหลังจากกดบันทึก Modal สัดส่วนเสร็จสิ้น
              }
            }}
          />
        )}
        {/* ========================================================================= */}
        {/* ⏳ LOADING OVERLAY (แสดงระหว่างรอผลยืนยันทำรายการ) */}
        {/* ========================================================================= */}
        {isSubmitting && (
          <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/70 animate-fadeIn">
            <style>{`
              @keyframes hourglass-rotate {
                0%, 40% { transform: rotate(0deg); }
                50%, 90% { transform: rotate(180deg); }
                100% { transform: rotate(360deg); }
              }
              @keyframes hourglass-sand-top {
                0% { transform: scaleY(1); }
                80%, 100% { transform: scaleY(0); }
              }
              @keyframes hourglass-sand-bottom {
                0% { transform: scaleY(0); }
                80%, 100% { transform: scaleY(1); }
              }
              @keyframes hourglass-stream {
                0%, 78% { opacity: 1; }
                80%, 100% { opacity: 0; }
              }
              @keyframes hourglass-glow {
                0% { transform: scale(1); opacity: 0.45; }
                100% { transform: scale(1.35); opacity: 0; }
              }
              @keyframes hourglass-progress {
                0% { transform: translateX(-100%); }
                100% { transform: translateX(250%); }
              }
              .hourglass-spin { animation: hourglass-rotate 2.4s ease-in-out infinite; will-change: transform; }
              .hourglass-sand-top { transform-origin: 32px 30px; transform-box: view-box; animation: hourglass-sand-top 1.2s linear infinite; }
              .hourglass-sand-bottom { transform-origin: 32px 54px; transform-box: view-box; animation: hourglass-sand-bottom 1.2s linear infinite; }
              .hourglass-stream { animation: hourglass-stream 1.2s linear infinite; }
              .hourglass-ring { animation: hourglass-glow 2.4s ease-out infinite; will-change: transform, opacity; }
              .hourglass-bar { animation: hourglass-progress 1.6s ease-in-out infinite; will-change: transform; }
            `}</style>
            <div className="w-full max-w-sm bg-[var(--bg-chart)] border border-[var(--border-item-light)] rounded-2xl shadow-2xl p-6 md:p-8 flex flex-col items-center gap-5 text-[var(--text-color)]">
              {/* นาฬิกาทราย */}
              <div className="relative w-24 h-24 rounded-full flex items-center justify-center bg-[var(--bg-item)] border-2 border-[var(--button-edit-bg)]">
                <span className="hourglass-ring absolute inset-0 rounded-full border-2 border-[var(--button-edit-bg)] pointer-events-none" />
                <svg className="hourglass-spin w-14 h-14" viewBox="0 0 64 64" fill="none" aria-hidden="true">
                  {/* ตัวแก้ว */}
                  <path d="M18 9 C18 22 30 28 30 32 C30 36 18 42 18 55 H46 C46 42 34 36 34 32 C34 28 46 22 46 9 Z" fill="var(--bg-chart)" stroke="var(--text-color)" strokeOpacity="0.55" strokeWidth="2.5" strokeLinejoin="round" />
                  {/* ทราย */}
                  <path className="hourglass-sand-top" d="M21 13 H43 C42 20 35 26 32 30 C29 26 22 20 21 13 Z" fill="var(--button-edit-bg)" />
                  <rect className="hourglass-stream" x="31.25" y="30" width="1.5" height="22" fill="var(--button-edit-bg)" />
                  <path className="hourglass-sand-bottom" d="M32 44 C36 47 42 50 43 54 H21 C22 50 28 47 32 44 Z" fill="var(--button-edit-bg)" />
                  {/* ฝาบน-ล่าง */}
                  <rect x="13" y="4" width="38" height="5" rx="2.5" fill="var(--button-edit-bg)" />
                  <rect x="13" y="55" width="38" height="5" rx="2.5" fill="var(--button-edit-bg)" />
                </svg>
              </div>

              <div className="text-center">
                <h3 className="text-lg md:text-xl font-black">กำลังประมวลผลรายการ</h3>
                <p className="mt-1 text-xs text-gray-400">
                  ส่งคำสั่งทั้งหมด <span className="font-bold text-[var(--yellow-text-color)]">{transactions.length}</span> รายการ กรุณาอย่าปิดหน้าต่างนี้
                </p>
              </div>

              {/* แถบโหลด */}
              <div className="w-full h-1.5 rounded-full overflow-hidden bg-[var(--bg-item)]">
                <div className="hourglass-bar h-full w-2/5 rounded-full bg-[var(--button-edit-bg)]" />
              </div>

              <div className="w-full rounded-lg px-4 py-3 text-xs md:text-sm text-center border bg-[var(--yellow-note-bg)] border-[var(--yellow-note-border)] text-[var(--yellow-text-color)]">
                <span className="font-bold">หมายเหตุ:</span> อาจใช้เวลาประมาณ 1 นาที หรือแล้วแต่จำนวนที่ทำรายการ...
              </div>
            </div>
          </div>
        )}
        {/* ========================================================================= */}
        {/* 🌟 SUCCESS SUMMARY POPUP (แสดงหลังทำรายการเสร็จ) */}
        {/* ========================================================================= */}
        {showSuccessSummary && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm animate-fadeIn">
            <div className="bg-[var(--bg-chart)] border border-[var(--border-item-light)] rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col transform transition-all scale-100">

              {/* Header */}
              <div className="p-6 bg-gradient-to-r from-green-500/20 to-emerald-500/10 border-b border-green-500/20 flex flex-col items-center justify-center text-center">
                <div className="w-16 h-16 bg-green-500 text-white rounded-full flex items-center justify-center text-3xl mb-3 shadow-[0_0_20px_rgba(34,197,94,0.4)]">
                  🎉
                </div>
                <h2 className="text-2xl font-black text-green-400">ยินดีด้วย! ทำรายการสำเร็จ</h2>
                <p className="text-sm opacity-80 mt-1" style={{ color: 'var(--text-color)' }}>สรุปการเปลี่ยนแปลงของหุ้นในพอร์ต</p>
              </div>

              {/* List of changes */}
              <div className="p-6 max-h-[50vh] overflow-y-auto space-y-4 custom-scrollbar">
                {successSummaryData.length === 0 ? (
                  <div className="text-center text-gray-400 text-sm py-4">ไม่มีการเปลี่ยนแปลงจำนวนหุ้น<br />(เป็นรายการ ฝาก/ถอน/ปันผล)</div>
                ) : (
                  successSummaryData.map((item, idx) => {
                    const isClosed = item.newQty <= 0;
                    // จำนวนหุ้นใหม่: เพิ่มขึ้น = เขียว, ลดลง = ส้ม, เท่าเดิม = สีปกติ
                    const qtyColor = item.newQty > item.oldQty
                      ? 'var(--green-text-color)'
                      : item.newQty < item.oldQty
                        ? 'var(--orange-text-color-light)'
                        : 'var(--text-color)';
                    // ต้นทุนเฉลี่ยใหม่: สูงขึ้น = ส้ม, ต่ำลง = เขียว, เท่าเดิม = สีปกติ (ไม่ใช่เขียว เพราะไม่ได้ดีขึ้นจริง)
                    const avgCostColor = item.newAvgPrice > item.oldAvgPrice
                      ? 'var(--orange-text-color-light)'
                      : item.newAvgPrice < item.oldAvgPrice
                        ? 'var(--green-text-color)'
                        : 'var(--text-color)';
                    const hasRealized = item.realizedGain !== null;
                    const isGain = hasRealized && (item.realizedGain as number) >= 0;
                    // กล่องทั้งใบ: แยกสีตามประเภทรายการ (ขาย = แดง, ซื้อ = เขียว) ไม่ใช่ตามกำไร/ขาดทุน — ใช้ความเข้ม (opacity) เท่ากันทั้งคู่ ไม่ให้ฝั่งใดหนักกว่ากัน
                    // กำไร/ขาดทุนจริงไปโชว์เฉพาะในกล่องเล็ก "กำไร/ขาดทุนจากการขาย" ด้านล่างเท่านั้น
                    const cardClass = item.hasSell
                      ? 'bg-red-500/10 border-red-500/40'
                      : item.hasBuy
                        ? 'bg-green-500/10 border-green-500/40'
                        : 'bg-[var(--bg-item)] border-[var(--border-item-light)]';
                    return (
                      <div key={idx} className={`relative border rounded-xl p-4 flex flex-col gap-3 shadow-sm ${cardClass}`}>
                        {/* มุมบนขวา: ป้าย "ขายหมดแล้ว" + SELL/BUY เรียงบรรทัดเดียวกัน (โชว์ SELL/BUY ทั้งคู่ได้ หากหุ้นนี้มีทั้งรายการซื้อและขายในชุดนี้) */}
                        <div className="absolute top-3 right-3 flex flex-row items-center gap-1">
                          {isClosed && (
                            <span className="bg-red-500/20 text-red-400 border border-red-500/30 text-[10px] font-black px-2 py-0.5 rounded">
                              ขายหมดแล้ว
                            </span>
                          )}
                          {item.hasSell && (
                            <span className="bg-red-500/20 text-red-400 border border-red-500/30 text-[10px] font-black px-2 py-0.5 rounded tracking-wide">
                              SELL
                            </span>
                          )}
                          {item.hasBuy && (
                            <span className="bg-green-500/20 text-green-400 border border-green-500/30 text-[10px] font-black px-2 py-0.5 rounded tracking-wide">
                              BUY
                            </span>
                          )}
                        </div>

                        <div className="flex justify-between items-center border-b border-[var(--border-item-light)] pb-2 pr-8">
                          <span className="text-xl font-black" style={{ color: 'var(--yellow-text-color)' }}>{item.symbol}</span>
                        </div>

                        <div className="rounded-lg overflow-hidden border" style={{ background: 'var(--component-bg-color)', borderColor: 'var(--border-item-light)' }}>
                          {/* หัวตาราง: ก่อนทำรายการ / หลังทำรายการ */}
                          <div className="grid items-center gap-2 px-3 pt-2.5 pb-2" style={{ gridTemplateColumns: '84px 1fr 28px 1fr' }}>
                            <span aria-hidden="true" />
                            <span className="text-xs font-bold uppercase text-center" style={{ color: 'var(--text-color)' }}>ก่อนทำรายการ</span>
                            <span aria-hidden="true" />
                            <span className="text-xs font-bold uppercase text-center" style={{ color: 'var(--green-text-color)' }}>หลังทำรายการ</span>
                          </div>

                          <div className="mx-4 h-px" style={{ background: 'var(--border-item-light)', opacity: 0.4 }} />

                          {/* จำนวนหุ้น */}
                          <div
                            className="grid items-center gap-2 px-3 py-2.5"
                            style={{ gridTemplateColumns: '84px 1fr 28px 1fr' }}
                          >
                            <span className="text-sm font-bold text-gray-200 border-r pr-2" style={{ borderColor: 'var(--border-item-light)' }}>
                              จำนวนหุ้น
                            </span>
                            <div className="text-center">
                              <span className="text-sm font-bold" style={{ color: 'var(--text-color)' }}>
                                {item.oldQty.toLocaleString(undefined, { maximumFractionDigits: 6 })}<span className="text-xs font-normal"> หุ้น</span>
                              </span>
                            </div>
                            <span className="text-4xl font-bold text-center" style={{ color: 'var(--yellow-text-color)' }} aria-hidden="true">→</span>
                            <div className="text-center">
                              <span className="text-xl font-black" style={{ color: qtyColor }}>
                                {item.newQty.toLocaleString(undefined, { maximumFractionDigits: 6 })}<span className="text-xs font-normal"> หุ้น</span>
                              </span>
                            </div>
                          </div>

                          <div className="mx-4 h-px" style={{ background: 'var(--border-item-light)', opacity: 0.4 }} />

                          {/* ต้นทุนเฉลี่ย */}
                          <div
                            className="grid items-center gap-2 px-3 py-2.5"
                            style={{ gridTemplateColumns: '84px 1fr 28px 1fr' }}
                          >
                            <span className="text-sm font-bold text-gray-200 border-r pr-2" style={{ borderColor: 'var(--border-item-light)' }}>
                              ต้นทุนเฉลี่ย
                            </span>
                            <div className="text-center">
                              <span className="text-sm font-bold" style={{ color: 'var(--text-color)' }}>
                                ${item.oldAvgPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                            <span className="text-4xl font-bold text-center" style={{ color: 'var(--yellow-text-color)' }} aria-hidden="true">→</span>
                            <div className="text-center">
                              <span className="text-xl font-black" style={{ color: avgCostColor }}>
                                ${item.newAvgPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                          </div>

                          <div className="mx-4 h-px" style={{ background: 'var(--border-item-light)', opacity: 0.4 }} />

                          {/* สัดส่วน */}
                          <div
                            className="grid items-center gap-2 px-3 py-2.5"
                            style={{ gridTemplateColumns: '84px 1fr 28px 1fr' }}
                          >
                            <span className="text-sm font-bold text-gray-200 border-r pr-2" style={{ borderColor: 'var(--border-item-light)' }}>
                              สัดส่วน
                            </span>
                            <div className="text-center">
                              <span className="text-sm font-bold" style={{ color: 'var(--text-color)' }}>{item.oldRatio.toFixed(2)}%</span>
                            </div>
                            <span className="text-4xl font-bold text-center" style={{ color: 'var(--yellow-text-color)' }} aria-hidden="true">→</span>
                            <div className="text-center">
                              <span className="text-xl font-black" style={{ color: 'var(--green-text-color)' }}>{item.newRatio.toFixed(2)}%</span>
                            </div>
                          </div>
                        </div>

                        {/* กำไร/ขาดทุนจากการขาย (เฉพาะรายการที่มีการขาย) */}
                        {hasRealized && (
                          <div
                            className="flex items-center justify-between gap-3 rounded-lg px-4 py-3 border"
                            style={{
                              background: isGain ? 'var(--pnl-gain-bg)' : 'var(--pnl-loss-bg)',
                              borderColor: isGain ? 'var(--pnl-gain-border)' : 'var(--pnl-loss-border)'
                            }}
                          >
                            <span className="text-xs font-bold text-white">กำไร/ขาดทุนจากการขาย</span>
                            <div className="text-right">
                              <div
                                className="text-xl md:text-2xl font-black drop-shadow-sm"
                                style={{ color: isGain ? 'var(--green-text-color)' : 'var(--red-text-color)' }}
                              >
                                {isGain ? '+' : '-'}${Math.abs(item.realizedGain as number).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </div>
                              {item.realizedGainPercent !== null && (
                                <div
                                  className="text-[11px] font-bold"
                                  style={{ color: isGain ? 'var(--green-text-color)' : 'var(--red-text-color)', opacity: 0.85 }}
                                >
                                  ({isGain ? '+' : '-'}{Math.abs(item.realizedGainPercent).toFixed(2)}%)
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    )
                  })
                )}
              </div>

              {/* Footer */}
              <div className="p-4 border-t flex justify-end bg-[var(--bg-item)]" style={{ borderColor: 'var(--border-item-light)' }}>
                <button
                  onClick={() => {
                    setShowSuccessSummary(false); // ปิด Popup นี้

                    // 💡 NOTE: ถ้าในอนาคตเปลี่ยนใจ "ไม่อยากให้ปิดหน้าต่างใหญ่" ให้มาลบบรรทัดข้างล่างนี้ออกนะครับ
                    onClose();
                  }}
                  className="px-6 py-2.5 font-black rounded-lg shadow-lg active:scale-95 transition-transform"
                  style={{ backgroundColor: 'var(--button-edit-bg)', color: 'var(--text-black)' }}
                >
                  ปิดหน้าต่าง
                </button>
              </div>
            </div>
          </div>
        )}
        {/* ========================================================================================= */}
        {/* ============================== 💻 DESKTOP VIEW ============================== */}
        {/* ========================================================================================= */}
        <div className="hidden md:flex flex-row w-full h-full">

          {/* ----- ฝั่งซ้าย Desktop ----- */}
          <div className="w-1/3 flex flex-col border-r h-full" style={{ borderColor: 'var(--border-item-light)', backgroundColor: 'var(--component-bg-color)' }}>
            <div className="p-5 border-b" style={{ borderColor: 'var(--border-item-light)' }}>
              <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                <span className="bg-yellow-400 text-black w-6 h-6 rounded-full flex items-center justify-center text-sm">1</span> เลือกพอร์ต & พอร์ตย่อย
              </h2>
              <div className="space-y-4">
                <div>
                  <select value={selectedPortId} onChange={(e) => handlePortChange(e.target.value)} className="w-full p-2.5 rounded-lg border focus:outline-none font-bold bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)]">
                    <option value="">-- เลือกพอร์ตหลัก --</option>
                    {renderPortfolioOptions()}
                  </select>

                  {selectedPortId && (
                    <div className="mt-2 p-2 rounded-lg flex flex-wrap items-center justify-between border shadow-sm gap-2 bg-[var(--component-bg-color)] border-[var(--border-item-light)]">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-[var(--secondary-text-color)]">เงินสด:</span>
                        {!isEditingCash ? (
                          <span className="text-sm font-bold text-green-500">${currentCash.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                        ) : (
                          <input type="number" value={cashInput} onChange={(e) => setCashInput(e.target.value)} placeholder="ยอดใหม่" className="p-1 rounded text-sm w-28 border font-bold focus:outline-none bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)]" />
                        )}
                      </div>
                      <div>
                        {!isEditingCash ? (
                          <button onClick={() => setIsManageCashOpen(true)} className="text-[10px] font-bold px-2 py-1 rounded transition-colors uppercase bg-[var(--button-edit-bg)] text-[var(--text-black)]">ปรับยอด</button>
                        ) : (
                          <div className="flex gap-1.5">
                            <button onClick={handleSaveCash} disabled={isAdjustingCash} className="text-[10px] font-bold px-2 py-1 rounded bg-green-500 text-white">{isAdjustingCash ? '...' : 'บันทึก'}</button>
                            <button onClick={() => setIsEditingCash(false)} disabled={isAdjustingCash} className="text-[10px] font-bold px-2 py-1 rounded bg-red-500 text-white">ยกเลิก</button>
                          </div>
                        )}
                      </div>
                    </div>

                  )}

                  {selectedPortId && currentPortQueueCount > 0 && (
                    <div className="flex items-center justify-between border-t pt-1.5 mt-1 border-[var(--border-item-light)] text-xs">
                      <span className="font-semibold text-gray-400">คงเหลือ (โดยคร่าว):</span>
                      <span className={`font-bold ${projectedCash < 0 ? 'text-red-400' : 'text-yellow-400'}`}>
                        ${projectedCash.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </span>
                    </div>
                  )}
                </div>

                {selectedPortfolio && subPorts.length > 0 && (
                  <div>
                    <select value={selectedSubPortId} onChange={(e) => setSelectedSubPortId(e.target.value)} className="w-full p-2.5 rounded-lg border focus:outline-none font-bold bg-[var(--bg-item)] text-[var(--text-color)] border-[var(--border-item-light)]">
                      <option value="">-- เลือกพอร์ตย่อย --</option>
                      {subPorts.map((sub: any) => <option key={sub.subPortId} value={sub.subPortId}>{sub.name}</option>)}
                    </select>
                  </div>
                )}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              <div className="flex justify-between items-center mb-3">
                <h3 className="text-sm font-bold flex items-center gap-2">
                  <span className="bg-yellow-400 text-black w-5 h-5 rounded-full flex items-center justify-center text-xs">2</span> เลือกหุ้นที่มีอยู่
                </h3>
                {/* ✅ ปุ่มเพิ่มหุ้นใหม่ Desktop */}
                <button
                  onClick={handleAddNewStockClick}
                  className="text-sm md:text-base font-bold px-4 py-2 rounded-lg bg-purple-500/20 text-purple-400 hover:bg-purple-500 hover:text-white transition-colors border border-purple-500/40 flex items-center gap-1.5 shadow-sm"
                >
                  + เพิ่มหุ้นใหม่
                </button>
              </div>
              {/* เพิ่ม Searchbar ตรงนี้ */}
              <input
                type="text"
                placeholder="ค้นหา Symbol..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full mb-3 p-2 text-sm rounded-lg border font-bold bg-[var(--bg-chart)] text-[var(--text-color)] border-[var(--border-item-light)] focus:outline-none focus:border-[#007aff]"
              />
              {filteredHoldings.length > 0 ? (
                <div className="grid grid-cols-1 gap-3">
                  {filteredHoldings.map((h: any, idx: number) => {
                    const stockSym = h.symbol || h.stockSymbol || h.assetSymbol;

                    const avgPrice = h.avgEntryPrice ?? h.avgBuyPrice ?? h.averagePrice ?? 0;

                    return (
                      <div
                        key={idx}
                        className="p-3 rounded-lg shadow-sm border bg-[var(--bg-item)] border-[var(--border-item-light)]"
                      >
                        <div className="flex justify-between items-center mb-2">
                          <span className="font-black text-lg text-[var(--tab-selected)]">{stockSym}</span>
                          <div className="flex flex-col items-end">
                            <span className="text-base font-medium opacity-80">@ {h.quantity} หุ้น</span>
                            {/* ✅ เพิ่มบรรทัดแสดงต้นทุนเฉลี่ย */}
                            <span className="text-sm  font-semibold" style={{ color: 'var(--yellow-text-color)' }}>
                              ราคาเฉลี่ย: ${Number(avgPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </div>
                        </div>

                        {/* ปุ่มซื้อเพิ่ม / ขายออก */}
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleActionClick(stockSym, 'BUY')}
                            className="flex-1 bg-green-500/10 text-green-500 hover:bg-green-500 hover:text-white font-bold py-1.5 rounded transition-colors"
                          >
                            ซื้อเพิ่ม
                          </button>
                          <button
                            onClick={() => handleActionClick(stockSym, 'SELL')}
                            className="flex-1 bg-red-500/10 text-red-500 hover:bg-red-500 hover:text-white font-bold py-1.5 rounded transition-colors"
                          >
                            ขายออก
                          </button>
                        </div>
                      </div>
                    )
                  })}
                </div>
              ) : (
                <div className="text-center p-4 opacity-50 text-sm border border-dashed rounded-lg border-[var(--border-item-light)]">ไม่มีหุ้นในพอร์ตย่อยนี้<br />(สามารถพิมพ์ชื่อหุ้นใหม่ด้านขวาได้เลย)</div>
              )}
            </div>
          </div>

          {/* ----- ฝั่งขวา Desktop ----- */}
          <div className="w-2/3 flex flex-col h-full bg-opacity-50">
            <div className="p-5 border-b flex justify-between items-center bg-black/10 border-[var(--border-item-light)]">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <span className="bg-yellow-400 text-black w-8 h-8 rounded-full flex items-center justify-center text-lg">3</span> ระบุรายละเอียด
              </h2>
              <button onClick={onClose} className="p-2 rounded-full hover:bg-red-500/20 text-gray-400 hover:text-red-500 transition-colors">✕</button>
            </div>

            <div className="p-5 flex-1 overflow-y-auto">
              {/* 💡 Note เตือนเรื่อง Broker Dime */}
              {/* <div className="mb-4 p-3 rounded-lg bg-yellow-500/10 border border-yellow-500/30 text-yellow-300 text-xs flex items-start gap-2 shadow-sm">
                <span className="text-base leading-none">⚠️</span>
                <div>
                  <span className="font-bold">หมายเหตุ:</span> สำหรับ Broker Dime ข้อมูลและการคำนวณอาจมีความคลาดเคลื่อนเล็กน้อย เนื่องจากระบบต้องใช้เวลาในการประมวลผลและไม่อัปเดตแบบ Real-time ทันที 100%
                </div>
              </div> */}
              {/* ✅ Form Desktop (อัปเดตเป็น Grid 5 คอลัมน์ ตามรูป) */}
              <div className={`grid grid-cols-5 gap-3 mb-6 p-4 rounded-xl border shadow-inner transition-all duration-300 border-[var(--border-item-light)] ${isNewStockForm
                ? 'bg-purple-500/15 border-purple-500/40' // 🟣 ถ้าเป็นหุ้นใหม่ ให้แสดงพื้นหลังและขอบสีม่วงอ่อน
                : type === 'BUY'
                  ? 'bg-[var(--green-text-color-light)]'
                  : isSellingAll
                    ? 'bg-[var(--orange-text-color-light)]'
                    : 'bg-[var(--red-text-color-light)]'
                } ${isAnimatingSymbol ? 'ring-2 ring-yellow-400 scale-[1.01]' : ''}`}>
                {/* --- Row 1 --- */}
                <div className="col-span-1">
                  <label className="flex items-center gap-1 text-xs font-bold mb-1.5 opacity-80">
                    วันที่ (Date) <span className="text-red-500">*</span>
                  </label>
                  <input type="date" value={date} max={getTodayStr()} onChange={(e) => handleDateChange(e.target.value)} className="w-full p-2.5 rounded-lg border font-bold bg-[var(--bg-chart)] text-[var(--text-color)] border-[var(--border-item-light)] focus:outline-none focus:border-[#007aff]" />
                </div>

                <div className="col-span-1">
                  <label className="flex items-center gap-1 text-xs font-bold mb-1.5 opacity-80">
                    ประเภทรายการ <span className="text-red-500">*</span>
                  </label>
                  <select value={type} onChange={(e) => setType(e.target.value as TransactionType)} className={`w-full p-2.5 rounded-lg border font-black transition-all bg-[var(--bg-chart)] text-[var(--text-color)] focus:outline-none focus:border-[#007aff] ${isAnimatingSymbol ? 'border-yellow-400 text-yellow-500' : 'border-[var(--border-item-light)]'}`}>
                    <option value="BUY">BUY (ซื้อ)</option>
                    <option value="SELL">SELL (ขาย)</option>
                  </select>
                </div>

                <div className="col-span-1 relative"> {/* ✅ เพิ่ม relative เพื่อคุมตำแหน่ง Dropdown */}
                  <label className="flex items-center gap-1 text-xs font-bold mb-1.5 opacity-80">
                    ตัวย่อหุ้น <span className="text-red-500">*</span>
                  </label>

                  <input
                    type="text"
                    className={`w-full p-2.5 rounded-lg border uppercase font-black transition-all bg-[var(--bg-chart)] text-[var(--text-color)] focus:outline-none focus:border-[#007aff] ${isAnimatingSymbol ? 'border-yellow-400 text-yellow-500 bg-yellow-400/10' : 'border-[var(--border-item-light)]'}`}
                    style={{ backgroundColor: 'var(--text-input)' }}
                    placeholder="เช่น AAPL, NVDA"
                    value={symbol}
                    onChange={handleSearchChange}
                    autoComplete="off"
                    onFocus={handleSearchFocus}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                  />

                  {/* ✅ Dropdown ที่กว้างเต็ม Input และอยู่เหนือทุกอย่าง */}
                  {showDropdown && (
                    <ul className="absolute left-0 w-full top-full mt-2 bg-[var(--bg-item)] border border-[var(--border-item-light)] rounded-lg shadow-2xl z-[500] max-h-60 overflow-y-auto">                      {searchQuery.length === 0 && !hotLoading && hotStocks.length > 0 && (
                      <li className="px-4 py-2 text-xs text-gray-500 bg-yellow-50 font-bold sticky top-0">🔥 Hot Trading</li>
                    )}
                      {searchQuery.length === 0 && hotLoading && (
                        <li className="px-4 py-2 text-xs text-gray-400">Loading...</li>
                      )}

                      {searchQuery.length === 0
                        ? hotStocks.map((item) => (
                          <li
                            key={item.symbol}
                            className="px-4 py-2 hover:bg-yellow-500/20 cursor-pointer text-gray-900 flex items-center gap-2 bg-yellow-50 font-bold"
                            onMouseDown={() => handleSelectSymbol(item.symbol)}
                          >
                            <span title="Hot" className="text-yellow-400">★</span>
                            <span className="font-semibold">{item.symbol}</span>
                            <span className="text-gray-500">${item.price.toFixed(2)}</span>
                          </li>
                        ))
                        : searchResults.map((item) => (
                          <li
                            key={item.symbol}
                            className={`px-4 py-2 hover:bg-yellow-500/20 cursor-pointer text-[var(--text-color)] flex items-center gap-2 ${POPULAR_SYMBOLS.includes(item.symbol) ? 'bg-yellow-500/10 font-bold' : ''}`}
                            onMouseDown={() => handleSelectSymbol(item.symbol)}
                          >
                            {POPULAR_SYMBOLS.includes(item.symbol) && (
                              <span title="Popular" className="text-yellow-400">★</span>
                            )}
                            <span className="font-semibold">{item.symbol}</span>
                            <span className="text-gray-400 text-xs">{item.name}</span>
                            {/* {item.price !== undefined && <span className="text-gray-500 ml-auto">${item.price.toFixed(2)}</span>} */}
                          </li>
                        ))
                      }
                      {loadingSearch && <li className="px-4 py-2 text-xs text-gray-400">กำลังค้นหา...</li>}
                    </ul>
                  )}
                </div>

                <div className="col-span-1 relative flex flex-col justify-end">
                  <div className="flex justify-between items-end mb-1.5">
                    <label className="flex items-center  text-xs font-bold opacity-80">
                      จำนวนหุ้น (Qty) <span className="text-red-500">*</span>
                    </label>
                    {/* {type === 'SELL' && currentHolding && currentHolding.quantity > 0 && (
                      <button onClick={() => setQuantity(currentHolding.quantity)}
                        style={{
                          backgroundColor: 'var(--component-bg-color)',
                          boxShadow: '0 10px 25px -3px rgba(0, 0, 0, 0.7), 0 4px 6px -4px rgba(0, 0, 0, 0.7)',
                          borderColor: 'var(--secondary-text-color)'
                        }}
                        className="text-[9px] rounded-md border shadow-xl p-1  font-bold text-red-500 hover:text-red-400 transition-colors active:scale-95 leading-none">
                        ขายทั้งหมด
                      </button>
                    )} */}
                  </div>
                  <input type="number" min="0" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value ? Number(e.target.value) : '')} placeholder="0.00" className="w-full p-2.5 rounded-lg border font-bold bg-[var(--bg-chart)] text-[var(--text-color)] border-[var(--border-item-light)] focus:outline-none focus:border-[#007aff]" />
                </div>

                <div className="col-span-1">
                  <label className="flex items-center gap-1 text-xs font-bold mb-1.5 opacity-80">
                    ราคา (Price) <span className="text-red-500">*</span>
                  </label>
                  <input type="number" min="0" step="any" value={price} onChange={(e) => setPrice(e.target.value ? Number(e.target.value) : '')} placeholder="0.00" className="w-full p-2.5 rounded-lg border font-bold bg-[var(--bg-chart)] text-[var(--text-color)] border-[var(--border-item-light)] focus:outline-none focus:border-[#007aff]" />
                </div>

                {/* --- Row 2 --- */}
                <div className="col-span-1 mt-1">
                  <label className="block text-xs font-bold mb-1.5 opacity-80">ค่าธรรมเนียม (Fee)</label>
                  <input type="number" min="0" step="any" value={fee} onChange={(e) => setFee(e.target.value ? Number(e.target.value) : '')} placeholder="0.00" className="w-full p-2.5 rounded-lg border font-bold bg-[var(--bg-chart)] text-[var(--text-color)] border-[var(--border-item-light)] focus:outline-none focus:border-[#007aff]" />
                </div>

                {(type === 'BUY' || type === 'SELL') && (
                  <div className="col-span-1 mt-1">
                    <label className="block text-xs font-bold mb-1.5 opacity-80">ภาษี (Tax)</label>
                    <input type="number" min="0" step="any" value={tax} onChange={(e) => setTax(e.target.value ? Number(e.target.value) : '')} placeholder="0.00" className="w-full p-2.5 rounded-lg border font-bold bg-[var(--bg-chart)] text-[var(--text-color)] border-[var(--border-item-light)] focus:outline-none focus:border-[#007aff]" />
                  </div>
                )}

                <div className={`flex justify-end items-end gap-2 mt-1 ${type === 'BUY' || type === 'SELL' ? 'col-span-3' : 'col-span-4'}`}>
                  {/* ✅ ปุ่ม "ขายทั้งหมด" จะโชว์เฉพาะตอนเลือก SELL และมีหุ้นในพอร์ต */}
                  {type === 'SELL' && currentHolding && currentHolding.quantity > 0 && (
                    <button
                      onClick={() => setQuantity(currentHolding.quantity)}
                      className="w-full text-sm md:w-auto px-6 py-2.5 h-[42px] rounded-lg font-bold shadow-lg transition-transform active:scale-95 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-500 text-white border border-red-500/50"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                      ขายทั้งหมด ({currentHolding.quantity})
                    </button>
                  )}

                  <button onClick={handleAddTransaction} className="w-full text-sm md:w-auto px-8 py-2.5 h-[42px] rounded-lg font-bold shadow-md transition-transform active:scale-95 flex items-center justify-center gap-2 bg-[#007aff] hover:bg-blue-600 text-white">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="3"><path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4"></path></svg>
                    เพิ่มลงคิวทำรายการ
                  </button>
                </div>
                {/* 📊 กล่องแสดงผลพรีวิวจำนวนหุ้นและราคาเฉลี่ยใหม่ */}
                {/* {symbol && type !== 'CASH_DEPOSIT' && type !== 'CASH_WITHDRAW' && (
                  <div className="col-span-full mt-2 p-3 rounded-lg border bg-yellow-500/10 border-yellow-500/30 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2 text-xs" style={{ backgroundColor: 'var(--component-bg-color)' }}>
                    <span className="font-bold text-yellow-400">
                      📊 ผลลัพธ์จำลอง ({symbol.toUpperCase()}): <span className="font-normal text-gray-300 text-[11px] ml-1">(เป็นการจำลองให้เห็นภาพการซื้อขาย อาจจะมีข้อผิดพลาดได้)</span>
                    </span>
                    <div className="flex items-center gap-4 font-bold shrink-0">
                      <span>จำนวนหุ้นรวม: <span className="text-white" style={{ color: 'var(--yellow-text-color)' }}>{simulatedHolding?.quantity.toLocaleString(undefined, { maximumFractionDigits: 4 })} หุ้น</span></span>
                      <span>ราคาเฉลี่ยใหม่: <span className="text-green-400" style={{ color: 'var(--green-text-color)' }}>${simulatedHolding?.avgPrice.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                    </div>
                  </div>
                )} */}
              </div>

              {/* Table Desktop */}
              <div className="flex items-center mb-3">
                <h3 className="font-bold text-base flex items-center gap-2">
                  <span className="bg-yellow-400 text-black w-5 h-5 rounded-full flex items-center justify-center text-xs">4</span> ตรวจสอบและยืนยัน
                </h3>
                <span className={`text-xs ml-2 font-bold px-2 py-0.5 rounded ${transactions.length >= 20 ? 'bg-red-500/20 text-red-500 animate-pulse' : 'bg-gray-800 text-gray-300'}`}>
                  ({transactions.length}/20 รายการ) {transactions.length >= 20 ? '- เต็มแล้ว' : ''}
                </span>
              </div>
              <div className="overflow-x-auto rounded-lg border shadow-sm border-[var(--border-item-light)]">
                <table className="w-full text-sm text-left whitespace-nowrap">
                  <thead className="uppercase font-bold bg-[var(--bg-item)]">
                    <tr>
                      <th className="px-4 py-3">ประเภท</th>
                      <th className="px-4 py-3">สัญลักษณ์</th>
                      <th className="px-4 py-3">วันที่</th>
                      <th className="px-4 py-3 text-right">รายะเอียด</th>
                      <th className="px-4 py-3 text-right">ค่าธรรมเนียม/ภาษี</th>
                      <th className="px-4 py-3 text-right text-[var(--yellow-text-color)]">ภาพรวม</th>
                      <th className="px-4 py-3 text-center">จัดการ</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.length === 0 ? (
                      <tr><td colSpan={7} className="text-center py-10 opacity-50 font-medium">ยังไม่มีรายการ</td></tr>
                    ) : (
                      transactions.map((t) => {
                        const qty = t.quantity || 0;
                        const prc = t.price || 0;
                        const tFee = t.fee || 0;
                        const tTax = t.tax || 0;
                        const totalValue = qty * prc;
                        const netAmount = t.type === 'BUY' ? totalValue + tFee + tTax : totalValue - tFee - tTax;
                        return (
                          <tr key={t.id} className={`border-t transition-colors border-[var(--border-item-light)] ${t.isNewStock ? 'bg-purple-500/15 hover:bg-purple-500/25' : 'hover:bg-black/5 dark:hover:bg-white/5'}`}>                            <td className="px-4 py-3 font-black" style={{ color: t.type === 'BUY' ? 'var(--green-btn)' : 'var(--red-text-color)' }}>{t.type}</td>
                            <td className="px-4 py-3 font-bold">
                              <div className="flex flex-col">
                                <div className="relative inline-block pr-3">
                                  {t.symbol || '-'}
                                  {t.isNewStock && (
                                    <span className="animate-pulse absolute -top-3 -right-3 bg-yellow-400 text-black text-[9px] font-black p-1 rounded shadow-sm scale-90 leading-none">
                                      NEW
                                    </span>
                                  )}
                                </div>
                                {/* ✅ แสดงชื่อพอร์ตย่อยใต้ Symbol สีตัวหนังสือจางลงเล็กน้อย */}
                                <span className="text-[11px] font-normal text-yellow-400/80 truncate max-w-[150px] block"
                                  title={`${t.portfolioName ? `${t.portfolioName} / ` : ''}${t.subPortName || '-'}`}
                                >
                                  {t.portfolioName ? `${t.portfolioName} / ` : ''}{t.subPortName || '-'}
                                </span>
                              </div>
                            </td>
                            <td className="px-4 py-3 opacity-80 text-xs">{new Date(t.date).toLocaleDateString()}</td>
                            <td className="px-4 py-3 text-right">
                              <div className="font-bold">{qty} หุ้น</div>
                              <div className="text-xs opacity-70">@ ${prc.toLocaleString()} (=${totalValue.toLocaleString()})</div>
                            </td>
                            <td className="px-4 py-3 text-right text-xs opacity-80">
                              <div>F: ${tFee.toLocaleString()}</div>
                              {(t.type === 'BUY' || t.type === 'SELL') && <div>T: ${tTax.toLocaleString()}</div>}
                            </td>
                            <td className="px-4 py-3 text-right font-black text-[var(--yellow-text-color)]">
                              ${netAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </td>
                            <td className="px-4 py-3 text-center flex items-center justify-center gap-1.5">
                              {/* ✅ ปุ่มแก้ไข */}
                              <button
                                onClick={() => handleEditTransaction(t)}
                                className="bg-yellow-500/10 text-yellow-500 font-bold px-3 py-1.5 rounded hover:bg-yellow-500 hover:text-black transition-colors"
                              >
                                แก้ไข
                              </button>
                              <button
                                onClick={() => handleRemoveTransaction(t.id)}
                                className="bg-red-500/10 text-red-500 font-bold px-3 py-1.5 rounded hover:bg-red-500 hover:text-white transition-colors"
                              >
                                ลบ
                              </button>
                            </td>
                          </tr>
                        )
                      })
                    )}
                  </tbody>
                </table>
              </div>
              {/* ✅ ส่วนสรุปยอดรวมใต้ตาราง */}
              {transactions.length > 0 && (
                <div className="mt-4 p-4 rounded-xl border flex flex-col md:flex-row justify-between items-center gap-3 bg-[var(--bg-item)] border-[var(--border-item-light)] text-xs md:text-sm font-bold">
                  <div className="flex items-center gap-6">
                    <span className="text-green-400">
                      รายการซื้อทั้งหมด: ${summaryTotals.totalBuy.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <span className="text-red-400">
                      รายการขายทั้งหมด: ${summaryTotals.totalSell.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                  <div className="text-gray-400 font-normal text-xs">
                    (คำนวณรวม Net Amount, Fee และ Tax เรียบร้อยแล้ว)
                  </div>
                </div>
              )}



            </div>

            {/* ----- Footer Desktop ----- */}
            <div className="p-5 border-t flex items-center justify-between bg-black/10 border-[var(--border-item-light)]">

              {/* ฝั่งซ้าย: ปุ่มแจ้งปัญหา */}
              <div className="w-fit min-w-[140px]">
                <ReportIssueButton
                  jobId="SELL_BUY_MODAL"
                  errorMessage="พบปัญหาในหน้าต่าง ซื้อ-ขาย หุ้น"
                  className="!w-full !py-2 !px-4 !rounded-lg"
                />
              </div>

              {/* ฝั่งขวา: ปุ่ม Action (ยกเลิก / ยืนยัน) */}
              <div className="flex gap-3">

                <button
                  type="button"
                  onClick={onClose}
                  className="px-5 py-2.5 rounded-lg font-bold  hover:bg-gray-500 transition-colors"
                  style={{ color: 'var(--text-color)' }}
                >
                  ยกเลิก
                </button>
                {/* <button onClick={onClose} className="px-6 py-3 rounded-lg font-bold transition-colors bg-[var(--button-bg-color)] text-[var(--text-color)]">ยกเลิก</button> */}
                <button onClick={handleSubmitAll} disabled={transactions.length === 0 || isSubmitting} className={`px-8 py-3 rounded-lg font-black shadow-xl transition-transform flex items-center justify-center gap-2 bg-[var(--button-edit-bg)] text-[var(--text-black)] ${transactions.length > 0 && !isSubmitting ? 'active:scale-95' : 'opacity-50 cursor-not-allowed'}`}>
                  {isSubmitting ? 'ประมวลผล...' : <> <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path></svg> ยืนยันทำรายการ ({transactions.length})</>}
                </button>
              </div>

            </div>
          </div>
        </div>


        {/* ========================================================================================= */}
        {/* ============================== 📱 MOBILE VIEW ============================== */}
        {/* ========================================================================================= */}
        <div className="flex md:hidden flex-col w-full h-full bg-[var(--component-bg-color)]">

          <div className="p-4 border-b flex justify-between items-center bg-black/10 shrink-0 border-[var(--border-item-light)]">
            <h2 className="text-xl font-bold">ทำรายการ S/B</h2>
            <button onClick={onClose} className="p-2 rounded-full hover:bg-red-500/20 text-gray-400 hover:text-red-500 transition-colors">✕</button>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-5 custom-scrollbar">

            {/* Mobile: เลือกพอร์ต */}
            <div className="bg-[var(--bg-item)] p-4 rounded-xl border border-[var(--border-item-light)]">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-bold mb-1 opacity-80">พอร์ตหลัก</label>
                  <select value={selectedPortId} onChange={(e) => handlePortChange(e.target.value)} className="w-full p-2.5 rounded-lg border focus:outline-none font-bold bg-[var(--component-bg-color)] text-[var(--text-color)] border-[var(--border-item-light)]">
                    <option value="">-- เลือกพอร์ตหลัก --</option>
                    {renderPortfolioOptions()}
                  </select>
                </div>
                {selectedPortfolio && subPorts.length > 0 && (
                  <div>
                    <label className="block text-xs font-bold mb-1 opacity-80">พอร์ตย่อย</label>
                    <select value={selectedSubPortId} onChange={(e) => setSelectedSubPortId(e.target.value)} className="w-full p-2.5 rounded-lg border focus:outline-none font-bold bg-[var(--component-bg-color)] text-[var(--text-color)] border-[var(--border-item-light)]">
                      <option value="">-- เลือกพอร์ตย่อย --</option>
                      {subPorts.map((sub: any) => <option key={sub.subPortId} value={sub.subPortId}>{sub.name}</option>)}
                    </select>
                  </div>
                )}
              </div>

              {selectedPortId && (
                <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t pt-3 border-[var(--border-item-light)]">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold opacity-80">เงินสด:</span>
                    {!isEditingCash ? (
                      <span className="text-sm font-bold text-green-500">${currentCash.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
                    ) : (
                      <input type="number" value={cashInput} onChange={(e) => setCashInput(e.target.value)} placeholder="ยอดใหม่" className="p-1 rounded text-sm w-24 border font-bold focus:outline-none bg-[var(--component-bg-color)] text-[var(--text-color)] border-[var(--border-item-light)]" />
                    )}
                  </div>
                  <div>
                    {!isEditingCash ? (
                      <button onClick={() => setIsManageCashOpen(true)} className="text-[10px] font-bold px-2 py-1 rounded bg-yellow-400/20 text-yellow-500 uppercase">ปรับยอด</button>
                    ) : (
                      <div className="flex gap-1.5">
                        <button onClick={handleSaveCash} disabled={isAdjustingCash} className="text-[10px] font-bold px-2 py-1 rounded bg-green-500 text-white">{isAdjustingCash ? '...' : 'บันทึก'}</button>
                        <button onClick={() => setIsEditingCash(false)} disabled={isAdjustingCash} className="text-[10px] font-bold px-2 py-1 rounded bg-red-500 text-white">ยกเลิก</button>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Mobile: Form */}
            <div className={`bg-[var(--bg-item)] p-4 rounded-xl border transition-all duration-300 border-[var(--border-item-light)] ${isAnimatingSymbol ? 'ring-2 ring-yellow-400 scale-[1.01] shadow-lg' : ''}`}>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <label className="block text-xs font-bold mb-1 opacity-80">ประเภทรายการ</label>
                  {/* ✅ เปลี่ยนเป็นแค่ ซื้อ / ขาย */}
                  <select value={type} onChange={(e) => setType(e.target.value as TransactionType)} className={`w-full p-2.5 rounded-lg border font-black transition-all bg-[var(--component-bg-color)] text-[var(--text-color)] ${isAnimatingSymbol ? 'border-yellow-400 text-yellow-500' : 'border-[var(--border-item-light)]'}`}>
                    <option value="BUY">BUY (ซื้อ)</option>
                    <option value="SELL">SELL (ขาย)</option>
                  </select>
                </div>

                <div className="col-span-2 relative">
                  <label className="block text-xs font-bold mb-1 opacity-80">ตัวย่อหุ้น</label>

                  <input
                    type="text"
                    value={symbol}
                    onChange={handleSearchChange}
                    onFocus={handleSearchFocus}
                    onBlur={() => setTimeout(() => setShowDropdown(false), 200)}
                    placeholder="AAPL"
                    autoComplete="off"
                    className={`w-full p-2.5 rounded-lg border uppercase font-black transition-all bg-[var(--component-bg-color)] text-[var(--text-color)] ${isAnimatingSymbol ? 'border-yellow-400 text-yellow-500 bg-yellow-400/10' : 'border-[var(--border-item-light)]'}`}
                  />

                  {/* Dropdown Mobile - แก้ไขให้สะอาด ไม่ซ้อน input */}
                  {showDropdown && (
                    <ul className="absolute left-0 w-full mt-1 bg-[var(--bg-item)] border border-[var(--border-item-light)] rounded-lg shadow-2xl z-[500] max-h-60 overflow-y-auto">
                      {searchQuery.length === 0 && !hotLoading && hotStocks.length > 0 && (
                        <li className="px-4 py-2 text-xs text-gray-500 bg-yellow-50 font-bold sticky top-0">🔥 Hot Trading</li>
                      )}
                      {searchQuery.length === 0
                        ? hotStocks.map((item) => (
                          <li
                            key={item.symbol}
                            className="px-4 py-2 hover:bg-yellow-500/20 cursor-pointer text-gray-900 flex items-center gap-2 bg-yellow-50 font-bold"
                            onMouseDown={() => {
                              setSymbol(item.symbol);
                              setShowDropdown(false);
                            }}
                          >
                            <span className="text-yellow-400">★</span>
                            <span className="font-semibold">{item.symbol}</span>
                            <span className="text-gray-500">${item.price.toFixed(2)}</span>
                          </li>
                        ))
                        : searchResults.map((item) => (

                          <li

                            key={item.symbol}

                            className={`px-4 py-2 hover:bg-yellow-500/20 cursor-pointer text-[var(--text-color)] flex items-center gap-2 ${POPULAR_SYMBOLS.includes(item.symbol) ? 'bg-yellow-500/10 font-bold' : ''}`}

                            onMouseDown={() => handleSelectSymbol(item.symbol)}

                          >

                            {POPULAR_SYMBOLS.includes(item.symbol) && (

                              <span title="Popular" className="text-yellow-400">★</span>

                            )}

                            <span className="font-semibold">{item.symbol}</span>

                            <span className="text-gray-400 text-xs">{item.name}</span>

                            {/* {item.price !== undefined && <span className="text-gray-500 ml-auto">${item.price.toFixed(2)}</span>} */}

                          </li>

                        ))

                      }

                      {loadingSearch && <li className="px-4 py-2 text-xs text-gray-400">กำลังค้นหา...</li>}

                    </ul>
                  )}
                </div>

                {/* Mobile: Quick Picks (Chips) */}
                {filteredHoldings.length > 0 && (
                  <div className="col-span-2 mt-1 mb-2">
                    <div className="text-[10px] font-bold opacity-60 mb-2">แตะเพื่อจัดการหุ้นในพอร์ต:</div>
                    <div className="flex overflow-x-auto gap-2 pb-2 no-scrollbar">
                      {filteredHoldings.map((h: any) => {
                        const stockSym = h.symbol || h.stockSymbol || h.assetSymbol;
                        const avgPrice = h.avgEntryPrice ?? h.avgBuyPrice ?? h.averagePrice ?? 0;

                        return (
                          <div key={stockSym} className="shrink-0 flex items-center border rounded-lg overflow-hidden border-[var(--border-item-light)] bg-[var(--button-bg-color)] shadow-sm">
                            <div className="px-3 py-2 flex flex-col border-r border-[var(--border-item-light)]">
                              <span className="text-xs font-black text-[var(--text-color)]">
                                {stockSym} <span className="opacity-60 ml-1 font-medium">@{h.quantity}</span>
                              </span>
                              {/* ✅ เพิ่มแสดงต้นทุนเฉลี่ยใน Mobile Chip */}
                              <span className="text-[10px]  font-semibold" style={{ color: 'var(--yellow-text-color)' }} >
                                ${Number(avgPrice).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </span>
                            </div>
                            <button onClick={() => handleActionClick(stockSym, 'BUY')} className="px-3 py-2 text-xs font-bold text-green-500 hover:bg-green-500/25 active:bg-green-500/30 transition-colors">
                              ซื้อ
                            </button>
                            <button onClick={() => handleActionClick(stockSym, 'SELL')} className="px-3 py-2 text-xs font-bold text-red-500 hover:bg-red-500/25 active:bg-red-500/30 transition-colors">
                              ขาย
                            </button>
                          </div>
                        )
                      })}
                    </div>
                  </div>
                )}

                <div className="col-span-2 border-t pt-3 border-[var(--border-item-light)]"></div>

                <div className="col-span-2">
                  <label className="block text-xs font-bold mb-1 opacity-80">วันที่ (Date)</label>
                  <input type="date" value={date} max={getTodayStr()} onChange={(e) => handleDateChange(e.target.value)} className="w-full p-2.5 rounded-lg border font-bold bg-[var(--component-bg-color)] text-[var(--text-color)] border-[var(--border-item-light)]" />
                </div>

                <div className="col-span-1">
                  <label className="flex justify-between items-end mb-1 text-xs font-bold opacity-80"><span>จำนวน (Qty)</span></label>
                  <input type="number" min="0" step="any" value={quantity} onChange={(e) => setQuantity(e.target.value ? Number(e.target.value) : '')} placeholder="0.00" className="w-full p-2.5 rounded-lg border font-bold bg-[var(--component-bg-color)] text-[var(--text-color)] border-[var(--border-item-light)]" />
                  {type === 'SELL' && currentHolding && currentHolding.quantity > 0 && (
                    <button onClick={() => setQuantity(currentHolding.quantity)} className="mt-1.5 w-full text-[10px] font-black bg-red-500/10 text-red-500 border border-red-500/30 py-1 rounded active:scale-95">ขายทั้งหมด ({currentHolding.quantity})</button>
                  )}
                </div>
                <div className="col-span-1">
                  <label className="block text-xs font-bold mb-1 opacity-80">ราคา (Price)</label>
                  <input type="number" min="0" step="any" value={price} onChange={(e) => setPrice(e.target.value ? Number(e.target.value) : '')} placeholder="0.00" className="w-full p-2.5 rounded-lg border font-bold bg-[var(--component-bg-color)] text-[var(--text-color)] border-[var(--border-item-light)]" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold mb-1 opacity-80">ธรรมเนียม (Fee)</label>
                  <input type="number" min="0" step="any" value={fee} onChange={(e) => setFee(e.target.value ? Number(e.target.value) : '')} placeholder="0.00" className="w-full p-2.5 rounded-lg border font-bold bg-[var(--component-bg-color)] text-[var(--text-color)] border-[var(--border-item-light)]" />
                </div>

                {(type === 'BUY' || type === 'SELL') && (
                  <div className="col-span-2">
                    <label className="block text-xs font-bold mb-1 opacity-80">ภาษี (Tax)</label>
                    <input type="number" min="0" step="any" value={tax} onChange={(e) => setTax(e.target.value ? Number(e.target.value) : '')} placeholder="0.00" className="w-full p-2.5 rounded-lg border font-bold bg-[var(--component-bg-color)] text-[var(--text-color)] border-[var(--border-item-light)]" />
                  </div>
                )}

                <div className="col-span-2 mt-3">
                  <button onClick={handleAddTransaction} className="w-full py-3 rounded-lg font-black shadow-md transition-transform active:scale-95 flex items-center justify-center gap-2" style={{ backgroundColor: 'var(--yellow-btn-bg)', color: '#000', backgroundImage: 'linear-gradient(to right, #facc15, #eab308)' }}>
                    เพิ่มลงคิวทำรายการ
                  </button>
                </div>
              </div>
            </div>

            {/* ✅ 5. ปรับ Mobile Queue ให้คำนวณและแสดงค่าสุทธิเหมือน Desktop */}
            {transactions.length > 0 && (
              <div>
                <h3 className="font-bold mb-3 text-sm">รายการรอยืนยัน ({transactions.length})</h3>
                <div className="space-y-2">
                  {transactions.map(t => {
                    const qty = t.quantity || 0;
                    const prc = t.price || 0;
                    const tFee = t.fee || 0;
                    const tTax = t.tax || 0;
                    const totalValue = qty * prc;
                    const netAmount = t.type === 'BUY' ? totalValue + tFee + tTax : totalValue - tFee - tTax;

                    return (
                      <div key={t.id} className={`p-3 rounded-lg border flex flex-col gap-2 border-[var(--border-item-light)] ${t.isNewStock ? 'bg-purple-500/20' : 'bg-[var(--bg-item)]'}`}>
                        <div className="flex justify-between items-start">
                          <div>
                            <div className="flex items-center gap-1.5">
                              <span className="font-black" style={{ color: t.type === 'BUY' ? 'var(--green-btn)' : 'var(--red-text-color)' }}>
                                {t.type}
                              </span>
                              <span className="font-bold text-[var(--text-color)] relative inline-block pr-3">
                                {t.symbol}
                                {t.isNewStock && (
                                  <span className="absolute -top-2 -right-3 bg-yellow-400 text-black text-[9px] font-black px-1 rounded shadow-sm scale-90 leading-none">
                                    NEW
                                  </span>
                                )}
                              </span>
                            </div>
                            {/* ✅ แสดงวันที่ทำรายการตรงนี้ */}
                            <div className="text-[11px] text-gray-400 font-medium">
                              📅 {new Date(t.date).toLocaleDateString('th-TH', { year: 'numeric', month: 'short', day: 'numeric' })}
                            </div>
                            <span className="text-[10px] font-normal text-yellow-400/80 truncate max-w-[200px] block"
                              title={`${t.portfolioName ? `${t.portfolioName} / ` : ''}${t.subPortName || '-'}`}>
                              {t.portfolioName ? `${t.portfolioName} / ` : ''}{t.subPortName || '-'}
                            </span>
                          </div>

                          <div className="flex gap-1.5">
                            <button
                              onClick={() => handleEditTransaction(t)}
                              className="text-yellow-500 text-xs font-bold bg-yellow-500/10 px-2 py-1 rounded hover:bg-yellow-500/20"
                            >
                              แก้ไข
                            </button>
                            <button
                              onClick={() => handleRemoveTransaction(t.id)}
                              className="text-red-500 text-xs font-bold bg-red-500/10 px-2 py-1 rounded hover:bg-red-500/20"
                            >
                              ลบ
                            </button>
                          </div>
                        </div>

                        <div className="flex justify-between text-xs opacity-80 mt-1">
                          <span>จำนวน: {qty} หุ้น @ ${prc}</span>
                          <span>มูลค่า: ${totalValue.toLocaleString()}</span>
                        </div>

                        <div className="flex justify-between items-center text-xs mt-1 border-t border-gray-500/20 pt-2">
                          <span className="opacity-60">F: ${tFee} {t.type === 'BUY' || t.type === 'SELL' ? `| T: $${tTax}` : ''}</span>
                          <span className="font-black text-[var(--yellow-text-color)] text-sm">
                            Net: ${netAmount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t flex flex-col justify-end gap-3 bg-black/10 shrink-0 border-[var(--border-item-light)]">
            <button onClick={handleSubmitAll} disabled={transactions.length === 0 || isSubmitting} className={`w-full py-3 rounded-lg font-black shadow-xl transition-transform flex items-center justify-center gap-2 bg-[var(--button-edit-bg)] text-[var(--text-black)] ${transactions.length > 0 && !isSubmitting ? 'active:scale-95' : 'opacity-50'}`}>
              {isSubmitting ? 'ประมวลผล...' : `ยืนยันทำรายการทั้งหมด (${transactions.length})`}
            </button>
            <button onClick={onClose} className="w-full py-3 rounded-lg font-bold bg-[var(--button-bg-color)] text-[var(--text-color)]">ยกเลิก</button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SellBuyStockModal;